/**
 * client/main.ts
 * Responsabilidad: Orquestador central del cliente.
 *
 * Conecta red, cámara, input y renderer en un bucle continuo de animación a 60 FPS
 * con interpolación de posición LERP exponencial y seguimiento suave de la cámara.
 */

import {
    connectToServer,
    connectToTrialRoom,
    createAuthClient,
    onRoomStateChange,
    getLocalSessionId,
    sendBoost,
    sendUsePotion,
    type IPlayerNetworkState,
    type IFoodNetworkState
} from "./network";
import { Camera } from "./camera";
import { Renderer, ELEMENT_COLORS, FOOD_COLORS, foodShapeFromId, type RenderablePlayer, type FoodRenderable } from "./renderer";
import { PixiRenderer } from "./pixi-renderer";
import { syncTitlesCatalogFromServer } from "./hologram-titles";

/** Bandera de migración para alternar entre el motor PixiJS (WebGL) y Canvas 2D nativo */
const USE_PIXI_RENDERER = true;
import { InputController } from "./input";
import { AudioManager } from "./audio";
import {
    type InterpolatedPlayer,
    type DeathAnimation,
    type ElementalHitEffect,
    type TrailPoint,
    type FoodParticle,
    type RespawnRing,
    type PowerUpRenderable,
    type PowerUpPickupEffect,
    createInterpolatedPlayer,
    INTERPOLATION_LAMBDA,
    type ClientEvent,
    type MeteorRender,
    type MeteorImpactEffect,
    type GravityCaptureEffect,
    type FloatingCombatText
} from "./types";
import { DEATH_ANIMATION_MS, ELEMENTAL_HIT_DURATION_MS, FOOD_MASS, ELEMENTAL_COUNTERS, POWERUP_CONFIG, type PowerUpType, MANUAL_ZOOM_STEP, ELEMENT_CHANGE_COOLDOWN_MS, BOOST_MIN_MASS, EMOTE_DURATION_MS } from "../src/shared/constants";
import { SKIN_IDS } from "./skins";
import {
    type PlayerCosmeticsConfig,
    type CosmeticCategory,
    DEFAULT_COSMETICS,
    sanitizeCosmetics,
    getBotCosmetics,
} from "./cosmetics";
import { MenuManager, parsePlayerCosmetics } from "./ui/menu";
import { ProfileManagerUI } from "./ui/profile";
import { AccountManager } from "./ui/account";
import { LeaderboardManager, fetchGlobalLeaderboard, renderGlobalLeaderboardTable, setCurrentLeaderboardMode } from "./ui/leaderboards";
import { QuestsManagerUI } from "./ui/quests";
import { PotionsUI } from "./ui/potions";
import { EmotesUI } from "./ui/emotes";
import { FamiliarsModalManager } from "./ui/familiars";
import { ElementSkinsModalManager } from "./ui/element-skins-modal";
import { questManager } from "./quests";
import { potionManager } from "./potions";
import { emoteManager } from "./emotes";
import {
    getElementCounters,
    hasSeenTutorial,
    markTutorialSeen,
} from "./onboarding";
import { safeGetStorage, safeSetStorage, sanitizeAlias, generateAnonAlias } from "./utils";
import { profileManager, formatTime } from "./profile";
import { tutorialManager } from "./tutorial";
import { initAnalytics, trackEvent } from "./analytics";
import { initPoki, gameplayStart, gameplayStop, isPokiReady, showRewardedAd, showCommercialBreak } from "./poki";
import Swal from "sweetalert2";
import i18next, { t } from "./i18n";
import {
    initCrazyGames,
    isCrazyGamesEnvironment,
    crazyGameplayStart,
    crazyGameplayStop,
    isCrazyGamesInstantMultiplayer,
    getCrazyGamesInviteRoomId,
    createCrazyGamesInviteLink,
    showCrazyGamesInviteButton,
    hideCrazyGamesInviteButton,
    registerCrazyGamesJoinListener,
} from "./crazygames";

interface BoostedReward {
    type: "speed" | "double-mass" | "triple-shield" | "invulnerability" | "healing";
    title: string;
    icon: string;
    bonus: string;
}

const REWARD_POOL: BoostedReward[] = [
    { type: "speed", title: "Súper Velocidad (20s)", icon: "⚡", bonus: "+50% masa inicial (150)" },
    { type: "double-mass", title: "Súper Absorción (25s)", icon: "✨", bonus: "+50% masa inicial (150)" },
    { type: "triple-shield", title: "Triple Escudo (3 impactos)", icon: "🛡️", bonus: "+50% masa inicial (150)" },
    { type: "invulnerability", title: "Inmunidad Total (12s)", icon: "⭐", bonus: "+50% masa inicial (150)" },
    { type: "healing", title: "Regeneración Instantánea", icon: "💚", bonus: "+150% masa inicial (250)" },
];

const REWARDED_COOLDOWN_MS = 180000; // 3 minutos
let lastRewardedUsedAt = 0;
let currentRewardedSelection: BoostedReward[] = [];
let countdownActive = false;
let countdownEndsAt = 0;
let countdownPaused = false;

let consecutiveDeaths = 0;
let lastCommercialBreakAt = 0;
let sessionStartTime = 0;
const COMMERCIAL_BREAK_DEATHS_THRESHOLD = 5;
const COMMERCIAL_BREAK_COOLDOWN_MS = 180000; // 3 minutos
const COMMERCIAL_BREAK_INITIAL_PROTECTION_MS = 60000; // 60 segundos

/**
 * Selecciona 2 recompensas distintas aleatoriamente del pool.
 */
function pickRandomRewards(): BoostedReward[] {
    const shuffled = [...REWARD_POOL].sort(() => Math.random() - 0.5);
    return [shuffled[0], shuffled[1]];
}

// Estado de jugadores, comidas y sesión en el cliente
const players: Map<string, InterpolatedPlayer> = new Map();
const foods: Map<string, { x: number; y: number }> = new Map();
const deaths: DeathAnimation[] = [];
const elementalHits: ElementalHitEffect[] = [];
let nextElementalHitId = 1;
const floatingTexts: FloatingCombatText[] = [];
let nextFloatingTextId = 1;
let spacebarHeld = false;
let localSessionId: string | null = null;
let isPrivateRoom = false;
let isTrialMode = false;
let activeTrialId: string | null = null;
let activeTrialPartyCode: string | null = null;
let currentTrialArenaWidth = 3000;
let currentTrialArenaHeight = 3000;

function getTrialArenaDimensions(trialId: string | null): { width: number; height: number } {
    switch (trialId) {
        case "trial_survival_dodge":
        case "trial_speed_collector":
            return { width: 2000, height: 2000 };
        case "trial_bot_hunt":
            return { width: 2400, height: 2400 };
        case "trial_mass_rush":
        case "trial_titan_slayer":
        case "trial_elemental_clash":
            return { width: 2200, height: 2200 };
        case "trial_coop_purge":
            return { width: 2600, height: 2600 };
        case "trial_pacifist":
            return { width: 1600, height: 1600 };
        default:
            return { width: 3000, height: 3000 };
    }
}
let finalRoomCode = "public";
let updatePrivateHudCount = (): void => {};
let lastFrameTime: number = 0;
let frameCount: number = 0;

// ==== Sprint 18.4: Attract Mode (Simulación en el menú) ====
interface AttractBot {
    id: string;
    x: number;
    y: number;
    targetX: number;
    targetY: number;
    mass: number;
    element: string;
    alive: boolean;
}
let attractBots: AttractBot[] = [];
let attractFoods: { x: number; y: number; shape: number }[] = [];
let attractModeActive = true;
let attractCameraAngle = 0;

function initAttractMode(): void {
    attractBots = [];
    const elements = ["fire", "water", "plant", "electric", "earth"];
    for (let i = 0; i < 22; i++) {
        const angle = (i / 22) * Math.PI * 2;
        const dist = 300 + Math.random() * 900;
        attractBots.push({
            id: `attract_${i}`,
            x: 1500 + Math.cos(angle) * dist,
            y: 1500 + Math.sin(angle) * dist,
            targetX: 1500 + Math.cos(angle + 1.2) * (dist * 0.8),
            targetY: 1500 + Math.sin(angle + 1.2) * (dist * 0.8),
            mass: 120 + Math.random() * 380,
            element: elements[i % 5],
            alive: true,
        });
    }
    attractFoods = [];
    for (let i = 0; i < 120; i++) {
        attractFoods.push({
            x: 1500 + (Math.random() - 0.5) * 2400,
            y: 1500 + (Math.random() - 0.5) * 2400,
            shape: Math.floor(Math.random() * 5),
        });
    }
}
initAttractMode();

// ==== Sprint 17: Modo Espectador ====
let isSpectating = false;
let spectatedSessionId: string | null = null;
let lastSurvivalTrackAt: number = 0;

/** Evento activo en el cliente (null si no hay) */
let activeEvent: ClientEvent | null = null;
const meteorImpacts: MeteorImpactEffect[] = [];
const gravityCaptures: GravityCaptureEffect[] = [];
const meteorImpactedSeen = new Set<string>();

/** Snapshot del ranking previo para anti-thrashing */
let lastRankingSnapshot: string = "";
/** Timestamp del último update del ranking */
let lastRankingUpdate: number = 0;
/** Intervalo de actualización del ranking (ms) */
const RANKING_UPDATE_INTERVAL_MS = 500;

let lastPlayTimeUpdate: number = 0;
const PLAY_TIME_UPDATE_INTERVAL_MS = 5000;

let movementAccumMs: number = 0;
let lastMouseMoveTime: number = 0;
let dangerTimeoutStart: number = 0;

/** Historial de puntos de estela por jugador */
const trails: Map<string, TrailPoint[]> = new Map();
/** Timestamps de pulso de absorción por jugador (para animación de escala) */
const absorbPulses: Map<string, number> = new Map();
/** Duración del pulso de absorción (ms) */
const ABSORB_PULSE_MS = 300;
const foodParticles: FoodParticle[] = [];
const respawnRings: RespawnRing[] = [];

/** Power-ups en el mapa, listos para renderizar */
const powerUps: Map<string, PowerUpRenderable> = new Map();
/** Efectos visuales de pickup activos */
const powerUpPickups: PowerUpPickupEffect[] = [];
/** ID incremental para los efectos de pickup */
let nextPickupEffectId = 1;

/** Emotes activos en pantalla por sessionId (Sprint 30) */
const activeEmotes = new Map<string, { emoteId: string; startTime: number; duration: number }>();

/** Pools preasignados reutilizables para erradicar la presión del GC a 60 FPS (Sprint 2) */
const pooledRenderablePlayers: RenderablePlayer[] = [];
const pooledRenderableFoods: FoodRenderable[] = [];
const pooledRenderablePowerUps: PowerUpRenderable[] = [];
const pooledRenderableMeteors: MeteorRender[] = [];

let menu!: MenuManager;
let familiarsModalManager!: FamiliarsModalManager;
let elementSkinsModalManager!: ElementSkinsModalManager;
let currentCosmetics: PlayerCosmeticsConfig = { ...DEFAULT_COSMETICS };

/** Ajusta la resolución del canvas y la escala del contexto para soporte HiDPI/Retina. */
function resizeCanvas(canvas: HTMLCanvasElement): void {
    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;

    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    // Evitar bloquear el canvas en modo 2D si se utiliza el motor WebGL de PixiJS
    if (!USE_PIXI_RENDERER) {
        const ctx = canvas.getContext("2d", { alpha: false });
        if (ctx) {
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }
    }
}

/** Interpola suavemente las posiciones de todos los jugadores hacia sus snapshots autoritativos. */
function interpolatePlayers(deltaTime: number): void {
    const alpha = 1 - Math.exp(-INTERPOLATION_LAMBDA * deltaTime);

    for (const player of players.values()) {
        if (!player.initialized) {
            player.renderX = player.targetX;
            player.renderY = player.targetY;
            player.vx = 0;
            player.vy = 0;
            player.initialized = true;
        } else {
            const prevX = player.renderX;
            const prevY = player.renderY;
            player.renderX += (player.targetX - player.renderX) * alpha;
            player.renderY += (player.targetY - player.renderY) * alpha;
            if (deltaTime > 0.0001) {
                player.vx = (player.renderX - prevX) / deltaTime;
                player.vy = (player.renderY - prevY) / deltaTime;
                const speed = Math.hypot(player.vx, player.vy);
                if (speed > 8) {
                    player.facingAngle = Math.atan2(player.vy, player.vx);
                }
            }
        }
    }
}

/** Punto de entrada principal que inicializa subsistemas y arranca el ciclo de vida del juego. */
async function main(): Promise<void> {
    sessionStartTime = performance.now();
    void syncTitlesCatalogFromServer();

    // Verificación de autenticación y carga de perfil desde Supabase si hay sesión activa
    const authClient = createAuthClient();
    const welcomeBonusBanner = document.getElementById("welcome-bonus-banner");
    try {
        const token = localStorage.getItem("alchem_auth_token") || authClient.auth.token;
        if (token) {
            welcomeBonusBanner?.classList.add("hidden");
            const res = await fetch("/api/profile", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const dbProfile = await res.json();
                if (dbProfile && dbProfile.userId) {
                    console.log("[Profile] Perfil cargado desde Supabase:", dbProfile);
                    profileManager.hydrate(dbProfile);
                    void emoteManager.refreshStatus();
                    if (dbProfile.alias && dbProfile.alias !== "Anon") {
                        safeSetStorage("alchem_alias", dbProfile.alias);
                    }
                    if (dbProfile.cosmetics && Object.keys(dbProfile.cosmetics).length > 0) {
                        localStorage.setItem("alchem_cosmetics", JSON.stringify(dbProfile.cosmetics));
                    }
                    window.dispatchEvent(new CustomEvent("alchem:auth_change", {
                        detail: {
                            isLoggedIn: true,
                            alias: dbProfile.alias || "Alquimista",
                            profile: dbProfile,
                            cosmetics: dbProfile.cosmetics
                        }
                    }));
                    if (dbProfile.welcomeBonusJustGranted) {
                        questManager.updateBalances(dbProfile.crystals, dbProfile.astralOrbs);
                        welcomeBonusBanner?.classList.add("hidden");
                        void Swal.fire({
                            title: "🎉 ¡Bono de Bienvenida Acreditado!",
                            text: "Por unirte a Alchem.io has recibido +200 💎 Cristales Alquímicos y +10 🔮 Piedras Filosofales en tu cuenta.",
                            icon: "success",
                            confirmButtonText: "¡Genial!",
                            confirmButtonColor: "#f59e0b",
                            background: "#0f172a",
                            color: "#f8fafc",
                        });
                    }

                    // Auto-reclamo de recompensas olvidadas de Tareas Diarias al iniciar sesión
                    try {
                        const unclaimedRes = await fetch("/api/trials/unclaimed", {
                            headers: { Authorization: `Bearer ${token}` },
                        });
                        if (unclaimedRes.ok) {
                            const unclaimedData = await unclaimedRes.json();
                            if (unclaimedData && Number(unclaimedData.claimedCount) > 0) {
                                if (typeof unclaimedData.newCrystals === "number" || typeof unclaimedData.newOrbs === "number") {
                                    questManager.updateBalances(
                                        unclaimedData.newCrystals ?? dbProfile.crystals,
                                        unclaimedData.newOrbs ?? dbProfile.astralOrbs
                                    );
                                }
                                void Swal.fire({
                                    icon: "success",
                                    title: "🎉 ¡Recompensas de Tareas Recuperadas!",
                                    html: `Por tareas completadas previamente has recibido <b>+${unclaimedData.totalCrystals} 💎 Cristales</b> y <b>+${unclaimedData.totalOrbs} 🔮 Orbes</b>.`,
                                    confirmButtonText: "¡Excelente!",
                                    confirmButtonColor: "#10b981",
                                    background: "#0f172a",
                                    color: "#f8fafc",
                                });
                            }
                        }
                    } catch (unclaimedErr) {
                        console.warn("[DailyTrials] No se pudo verificar recompensas pendientes:", unclaimedErr);
                    }
                }
            } else if (res.status === 401) {
                // Token expirado o inválido: limpiar sesión local
                localStorage.removeItem("alchem_auth_token");
                localStorage.removeItem("colyseus-auth-token");
                profileManager.resetProfile();
                welcomeBonusBanner?.classList.remove("hidden");
            }
        } else {
            welcomeBonusBanner?.classList.remove("hidden");
        }
    } catch (e) {
        console.warn("[Auth] No se pudo verificar la sesión con el servidor:", e);
        welcomeBonusBanner?.classList.remove("hidden");
    }

    // Escuchar cambios de autenticación globales para controlar el banner
    window.addEventListener("alchem:auth_change", (e: any) => {
        const banner = document.getElementById("welcome-bonus-banner");
        if (e.detail?.isLoggedIn) {
            banner?.classList.add("hidden");
        } else {
            banner?.classList.remove("hidden");
        }
    });


    // Inicializar analytics, CrazyGames y Poki
    initAnalytics();
    await initCrazyGames();

    const invitedRoomCode = getCrazyGamesInviteRoomId();
    const instantMultiplayer = isCrazyGamesInstantMultiplayer();
    const isInstantEntry = Boolean(invitedRoomCode) || instantMultiplayer;

    // Determinar el código de sala a usar:
    // - Si viene por link de invitación (`invitedRoomCode`), usa ese código para caer en la sala del amigo.
    // - Si es `instantMultiplayer` ("Play with Friends"), genera un código nuevo único para esta fiesta.
    // - Si es partida normal, usa `"public"` para que todos entren a la arena principal y puedan invitar amigos a ella.
    const activeInviteCode: string = invitedRoomCode
        ? invitedRoomCode
        : instantMultiplayer
        ? `cg_${Math.random().toString(36).substring(2, 9)}`
        : "public";

    registerCrazyGamesJoinListener((newRoomCode) => {
        const url = new URL(window.location.href);
        url.searchParams.set("roomId", newRoomCode);
        window.location.href = url.toString();
    });

    await initPoki();

    // Detección de dispositivo táctil / móvil (Sprint 16)
    const isTouchDevice = ("ontouchstart" in window)
        || (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 0)
        || /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent);
    // Ocultar siempre el antiguo aviso de bloqueo móvil si existe en el DOM
    const mobileWarning = document.getElementById("mobile-warning");
    if (mobileWarning) {
        mobileWarning.classList.add("hidden");
    }

    const canvas = document.getElementById("game-canvas") as HTMLCanvasElement | null;
    if (!canvas) {
        throw new Error("Elemento '#game-canvas' no encontrado en el DOM.");
    }

    const overlay = document.getElementById("connection-overlay");
    const connectionStatus = document.getElementById("connection-status");
    const massDisplay = document.getElementById("mass-display");
    const elementDisplay = document.getElementById("element-display");
    const elementCooldown = document.getElementById("element-cooldown");
    const playersDisplay = document.getElementById("players-display");
    const deathOverlay = document.getElementById("death-overlay");
    const respawnCountdown = document.getElementById("respawn-countdown");
    const reviveInstantBtn = document.getElementById("revive-instant-btn") as HTMLButtonElement | null;
    const rewardedOpt1 = document.getElementById("rewarded-opt-1") as HTMLButtonElement | null;
    const rewardedOpt2 = document.getElementById("rewarded-opt-2") as HTMLButtonElement | null;
    const rewardedCardsContainer = document.getElementById("rewarded-cards-container");
    const rewardedCooldownBadge = document.getElementById("rewarded-cooldown-badge");
    const rewardedCdTimer = document.getElementById("rewarded-cd-timer");
    const opt1Icon = document.getElementById("opt-1-icon");
    const opt1Title = document.getElementById("opt-1-title");
    const opt2Icon = document.getElementById("opt-2-icon");
    const opt2Title = document.getElementById("opt-2-title");
    const spawnTooltip = document.getElementById("spawn-tooltip");
    const spawnElement = document.getElementById("spawn-element");
    const spawnHunt = document.getElementById("spawn-hunt");
    const spawnFlee = document.getElementById("spawn-flee");
    const huntTarget = document.getElementById("hunt-target");
    const fleeTarget = document.getElementById("flee-target");
    const killsDisplay = document.getElementById("kills-display");
    const deathsDisplay = document.getElementById("deaths-display");
    const streakDisplay = document.getElementById("streak-display");
    const streakValue = document.getElementById("streak-value");
    const audioIcon = document.getElementById("audio-icon");
    const audioToggle = document.getElementById("audio-toggle");
    const audioGroup = document.getElementById("audio-group");
    const volumePopover = document.getElementById("volume-popover");
    const volumeSlider = document.getElementById("volume-slider") as HTMLInputElement | null;
    const volumeValueText = document.getElementById("volume-value-text");
    const buffHud = document.getElementById("buff-hud");

    const helpToggle = document.getElementById("help-toggle");
    const helpPanel = document.getElementById("help-panel");

    const eventBanner = document.getElementById("event-banner");
    const eventBannerIcon = document.getElementById("event-banner-icon");
    const eventBannerTitle = document.getElementById("event-banner-title");
    const eventBannerSubtitle = document.getElementById("event-banner-subtitle");
    let eventWarningInterval: ReturnType<typeof setInterval> | null = null;

    // ==== Sprint 17: Elementos del Modo Espectador ====
    const spectatorBar = document.getElementById("spectator-bar");
    const spectatorPrevBtn = document.getElementById("spectator-prev-btn");
    const spectatorNextBtn = document.getElementById("spectator-next-btn");
    const spectatorJoinBtn = document.getElementById("spectator-join-btn");
    const spectatorTargetName = document.getElementById("spectator-target-name");
    const deathSpectateBtn = document.getElementById("death-spectate-btn");
    const mobileBoostBtn = document.getElementById("mobile-boost-btn");
    const mobileElementBar = document.getElementById("mobile-element-bar");

    const applyStaticTranslations = (): void => {
        const accountBtnIcon = document.querySelector("#account-btn .hud-btn-icon, #profile-btn .hud-btn-icon");
        if (accountBtnIcon) accountBtnIcon.textContent = "👤 CUENTA";
        const helpBtnIcon = document.querySelector("#help-toggle .hud-btn-icon");
        if (helpBtnIcon) helpBtnIcon.textContent = t("hud.help");

        const counterLabels = document.querySelectorAll(".counter-label");
        if (counterLabels.length >= 2) {
            counterLabels[0].textContent = t("hud.hunts");
            counterLabels[1].textContent = t("hud.fleesFrom");
        }

        const aliasTagline = document.querySelector(".alias-modal-tagline");
        if (aliasTagline) aliasTagline.textContent = "Arena Alquímica Multijugador";
        const aliasTitle = document.querySelector(".alias-modal-title");
        if (aliasTitle) aliasTitle.textContent = t("modals.chooseIdentity");
        const aliasSkinLabel = document.querySelector(".alias-skin-label");
        if (aliasSkinLabel) aliasSkinLabel.textContent = t("modals.chooseSkin");
        const aInput = document.getElementById("alias-input") as HTMLInputElement | null;
        if (aInput) aInput.placeholder = t("modals.aliasPlaceholder");
        const aSubmit = document.getElementById("alias-submit");
        if (aSubmit) aSubmit.textContent = t("modals.enterArena");
        const aSpectate = document.getElementById("alias-spectate-btn") || document.getElementById("spectator-btn");
        if (aSpectate) aSpectate.textContent = t("modals.enterAsSpectator");
        const aliasHint = document.querySelector(".alias-modal-hint");
        if (aliasHint) aliasHint.innerHTML = t("modals.anonHint");

        const deathTitle = document.querySelector(".death-title");
        if (deathTitle) deathTitle.textContent = t("modals.youHaveBeenDevoured");
        const reviveFreeTitle = document.querySelector("#revive-instant-btn .revive-btn-title");
        if (reviveFreeTitle) reviveFreeTitle.textContent = t("modals.reviveFree");
        const reviveFreeSub = document.querySelector("#revive-instant-btn .revive-btn-subtitle");
        if (reviveFreeSub) reviveFreeSub.textContent = t("modals.reviveFreeSub");
        const reviveFreeAction = document.querySelector("#revive-instant-btn .revive-btn-action");
        if (reviveFreeAction) reviveFreeAction.textContent = t("modals.respawnAction");
        const deathSpectateTitle = document.querySelector("#death-spectate-btn .revive-btn-title");
        if (deathSpectateTitle) deathSpectateTitle.textContent = t("modals.staySpectator");
        const deathSpectateSub = document.querySelector("#death-spectate-btn .revive-btn-subtitle");
        if (deathSpectateSub) deathSpectateSub.textContent = t("modals.staySpectatorSub");
        const deathSpectateAction = document.querySelector("#death-spectate-btn .revive-btn-action");
        if (deathSpectateAction) deathSpectateAction.textContent = t("modals.watchAction");
        const reviveDivider = document.querySelector(".revive-divider span");
        if (reviveDivider) reviveDivider.textContent = t("modals.watchAdBonus");

        const profileTitle = document.querySelector(".profile-modal-title");
        if (profileTitle) profileTitle.textContent = t("modals.profileTitle");
        const profileChangeBtn = document.getElementById("profile-change-alias");
        if (profileChangeBtn) profileChangeBtn.textContent = t("modals.changeAlias");
        const profileResetBtn = document.getElementById("profile-reset");
        if (profileResetBtn) profileResetBtn.textContent = t("modals.resetProgress");
        const profileHint = document.querySelector(".profile-modal-hint");
        if (profileHint) profileHint.textContent = t("modals.profileCloseHint");

        const spectatorBadge = document.querySelector(".spectator-badge");
        if (spectatorBadge) spectatorBadge.textContent = `👁️ ${t("hud.spectatorBadge")}`;
        if (spectatorJoinBtn) spectatorJoinBtn.textContent = `⚔️ ${t("hud.playNow")}`;
    };
    applyStaticTranslations();

    resizeCanvas(canvas);
    const camera = new Camera(window.innerWidth, window.innerHeight);
    const activeRenderer: PixiRenderer | Renderer = USE_PIXI_RENDERER
        ? new PixiRenderer(canvas, camera)
        : new Renderer(canvas, camera);

    if (activeRenderer instanceof PixiRenderer) {
        await activeRenderer.init();
    }
    let room!: Awaited<ReturnType<typeof connectToServer>>;
    let inputController: InputController | null = null;
    const audio = new AudioManager() as AudioManager & { setMuted: (muted: boolean) => void };
    audio.setMuted = (muted: boolean): void => {
        if (audio.isMuted() !== muted) {
            audio.toggleMute();
        }
    };

    // Zoom manual con la rueda del mouse
    canvas.addEventListener("wheel", (e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? MANUAL_ZOOM_STEP : -MANUAL_ZOOM_STEP;
        camera.adjustManualZoom(delta);
    }, { passive: false });

    if (audioIcon) {
        audioIcon.textContent = audio.isMuted() ? "🔇" : "🔊";
        audioIcon.classList.toggle("muted", audio.isMuted());
    }

    window.addEventListener("resize", () => {
        resizeCanvas(canvas);
        camera.setViewport(window.innerWidth, window.innerHeight);
        if (activeRenderer instanceof PixiRenderer) {
            activeRenderer.resize(window.innerWidth, window.innerHeight);
        }
    });

    // Activar audio al primer gesto del usuario
    const activateAudio = (): void => {
        audio.start();
        window.removeEventListener("pointerdown", activateAudio);
        window.removeEventListener("keydown", activateAudio);
    };
    window.addEventListener("pointerdown", activateAudio, { once: true });
    window.addEventListener("keydown", activateAudio, { once: true });

    // Managers modulares de UI (Menú, Perfil, Rankings)
    menu = new MenuManager(() => audio.start());
    familiarsModalManager = new FamiliarsModalManager();
    familiarsModalManager.setMenu(menu);
    elementSkinsModalManager = new ElementSkinsModalManager();
    elementSkinsModalManager.setMenu(menu);
    currentCosmetics = menu.getCosmetics();

    async function refreshMainMenuIndexState(): Promise<void> {
        const token = localStorage.getItem("alchem_auth_token") || authClient.auth.token;
        const banner = document.getElementById("welcome-bonus-banner");
        if (token) {
            banner?.classList.add("hidden");
            try {
                const res = await fetch("/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const dbProfile = await res.json();
                    if (dbProfile && dbProfile.userId) {
                        profileManager.hydrate(dbProfile);
                        questManager.updateBalances(dbProfile.crystals, dbProfile.astralOrbs);
                        if (dbProfile.equippedFamiliar) {
                            menu.setFamiliar(dbProfile.equippedFamiliar);
                            currentCosmetics = menu.getCosmetics();
                        }
                    }
                }
            } catch {
                // ignore network error on soft refresh
            }
            void questManager.fetchServerState();
            void potionManager.refreshStatus();
            void emoteManager.refreshStatus();
            void familiarsModalManager.refreshUserData();
            void elementSkinsModalManager.refreshUserData();
        } else {
            banner?.classList.remove("hidden");
        }
        menu.updateHeroHeaderState();
        const activeCat = (document.querySelector(".cosmetics-tab-btn.active") as HTMLElement)?.dataset.category as any || "eyes";
        menu.renderCategoryOptions(activeCat);
        menu.renderStudioPreview();
    }

    function reloadIfReturnedToMainMenu(): void {
        setTimeout(() => {
            if (!document.body.classList.contains("in-menu")) return;
            const isModalVisible = (id: string) => {
                const el = document.getElementById(id);
                return el !== null && !el.classList.contains("hidden");
            };
            if (
                !isModalVisible("account-modal") &&
                !isModalVisible("potions-modal") &&
                !isModalVisible("quests-modal") &&
                !isModalVisible("profile-modal") &&
                !isModalVisible("tutorial-overlay")
            ) {
                void refreshMainMenuIndexState();
            }
        }, 80);
    }

    window.addEventListener("alchem:refresh_index", () => {
        void refreshMainMenuIndexState();
    });

    // Sprint 35: Sincronización en tiempo real del título honorífico equipado
    window.addEventListener("alchem:title-equipped", (e: any) => {
        const titleId = e.detail?.titleId || "";
        if (room) {
            try {
                room.send("set-title", { titleId });
            } catch (err) {
                console.warn("[Titles] Error enviando set-title al room:", err);
            }
        }
        if (localSessionId && players.has(localSessionId)) {
            const localPlayer = players.get(localSessionId)!;
            localPlayer.equippedTitle = titleId;
        }
    });

    /** Sprint 33: Helper para salir limpiando parámetros de invitación de la URL */
    function leaveToMainMenu(): void {
        try {
            window.history.replaceState({}, document.title, window.location.pathname);
        } catch {
            // ignore
        }
        window.location.href = window.location.origin + window.location.pathname;
    }

    // Sprint 33: Controles del configurador de Sala Privada (Duración y Metas)
    const durationPillBtns = document.querySelectorAll<HTMLButtonElement>("#private-duration-selector .private-pill-btn");
    const durationInput = document.getElementById("private-duration-val") as HTMLInputElement | null;
    durationPillBtns.forEach((btn) => {
        btn.addEventListener("click", () => {
            durationPillBtns.forEach((b) => b.classList.remove("active"));
            btn.classList.add("active");
            if (durationInput) {
                durationInput.value = btn.getAttribute("data-duration") || "3";
            }
        });
    });

    const goalTypeSelect = document.getElementById("private-goal-type-select") as HTMLSelectElement | null;
    const goalKillsWrap = document.getElementById("private-goal-kills-wrap");
    const goalMassWrap = document.getElementById("private-goal-mass-wrap");

    const updateGoalWrappers = (): void => {
        const val = goalTypeSelect ? goalTypeSelect.value : "time";
        const showKills = val === "kills" || val === "both";
        const showMass = val === "mass" || val === "both";

        if (goalKillsWrap) {
            if (showKills) {
                goalKillsWrap.classList.remove("hidden");
            } else {
                goalKillsWrap.classList.add("hidden");
            }
            goalKillsWrap.style.display = showKills ? "flex" : "none";
        }

        if (goalMassWrap) {
            if (showMass) {
                goalMassWrap.classList.remove("hidden");
            } else {
                goalMassWrap.classList.add("hidden");
            }
            goalMassWrap.style.display = showMass ? "flex" : "none";
        }
    };
    goalTypeSelect?.addEventListener("change", updateGoalWrappers);
    document.getElementById("create-private-room-btn")?.addEventListener("click", () => {
        updateGoalWrappers();
    });
    updateGoalWrappers();

    const profileUI = new ProfileManagerUI({
        onOpen: () => hideCrazyGamesInviteButton(),
        onClose: () => {
            if (localSessionId && players.has(localSessionId) && players.get(localSessionId)!.alive && !isSpectating) {
                showCrazyGamesInviteButton(activeInviteCode);
            }
            reloadIfReturnedToMainMenu();
        }
    });

    const accountManager = new AccountManager(authClient, {
        onOpen: () => hideCrazyGamesInviteButton(),
        onClose: () => {
            if (localSessionId && players.has(localSessionId) && players.get(localSessionId)!.alive && !isSpectating) {
                showCrazyGamesInviteButton(activeInviteCode);
            }
            reloadIfReturnedToMainMenu();
        }
    });

    const aliasAccountBtn = document.getElementById("alias-account-btn");
    if (aliasAccountBtn) {
        aliasAccountBtn.addEventListener("click", () => {
            accountManager.open();
        });
    }

    const openRouletteBtn = document.getElementById("open-roulette-btn");
    if (openRouletteBtn) {
        openRouletteBtn.addEventListener("click", () => {
            accountManager.open("roulette");
        });
    }

    const welcomeBonusCtaBtn = document.getElementById("welcome-bonus-cta-btn");
    if (welcomeBonusCtaBtn) {
        welcomeBonusCtaBtn.addEventListener("click", () => {
            accountManager.open("login");
            accountManager.switchAuthMode("register");
        });
    }

    const leaderboardUI = new LeaderboardManager();

    // Conexión del conmutador de modo de ranking (🔥 Temporada vs 🏛️ Histórico)
    const btnModeSeason = document.getElementById("leaderboard-mode-season");
    const btnModeAllTime = document.getElementById("leaderboard-mode-alltime");
    const killsTbody = document.getElementById("account-leaderboard-kills-tbody");
    const massTbody = document.getElementById("account-leaderboard-mass-tbody");
    const streakTbody = document.getElementById("account-leaderboard-streak-tbody");
    const timeTbody = document.getElementById("account-leaderboard-time-tbody");
    const leaderboardLoading = document.getElementById("account-leaderboard-loading");

    const reloadLeaderboardTables = async (mode: "season" | "alltime") => {
        setCurrentLeaderboardMode(mode);
        if (btnModeSeason && btnModeAllTime) {
            if (mode === "season") {
                btnModeSeason.classList.add("active");
                btnModeSeason.style.background = "rgba(245,158,11,0.2)";
                btnModeSeason.style.borderColor = "#f59e0b";
                btnModeSeason.style.color = "#fde047";

                btnModeAllTime.classList.remove("active");
                btnModeAllTime.style.background = "";
                btnModeAllTime.style.borderColor = "";
                btnModeAllTime.style.color = "";
            } else {
                btnModeAllTime.classList.add("active");
                btnModeAllTime.style.background = "rgba(245,158,11,0.2)";
                btnModeAllTime.style.borderColor = "#f59e0b";
                btnModeAllTime.style.color = "#fde047";

                btnModeSeason.classList.remove("active");
                btnModeSeason.style.background = "";
                btnModeSeason.style.borderColor = "";
                btnModeSeason.style.color = "";
            }
        }

        if (leaderboardLoading) leaderboardLoading.classList.remove("hidden");
        const data = await fetchGlobalLeaderboard(mode);
        if (leaderboardLoading) leaderboardLoading.classList.add("hidden");

        if (data) {
            renderGlobalLeaderboardTable(killsTbody, data.topByKills || [], "totalKills");
            renderGlobalLeaderboardTable(massTbody, data.topByMass || [], "bestMass");
            renderGlobalLeaderboardTable(streakTbody, data.topByStreak || [], "bestStreak");
            renderGlobalLeaderboardTable(timeTbody, data.topByTime || [], "totalTimeMs");
        }
    };

    if (btnModeSeason) {
        btnModeSeason.addEventListener("click", () => {
            void reloadLeaderboardTables("season");
        });
    }
    if (btnModeAllTime) {
        btnModeAllTime.addEventListener("click", () => {
            void reloadLeaderboardTables("alltime");
        });
    }

    const questsUI = new QuestsManagerUI({
        onOpen: () => hideCrazyGamesInviteButton(),
        onClose: () => {
            if (localSessionId && players.has(localSessionId) && players.get(localSessionId)!.alive && !isSpectating) {
                showCrazyGamesInviteButton(activeInviteCode);
            }
            reloadIfReturnedToMainMenu();
        }
    });

    // Sprint 29: Interfaz de Usuario para Pociones y Consumibles Tácticos
    const potionsUI = new PotionsUI({
        onUsePotion: (slotIndex: number) => {
            if (room && localSessionId && players.has(localSessionId)) {
                sendUsePotion(room, slotIndex);
            }
        },
        onOpen: () => hideCrazyGamesInviteButton(),
        onClose: () => {
            if (localSessionId && players.has(localSessionId) && players.get(localSessionId)!.alive && !isSpectating) {
                showCrazyGamesInviteButton(activeInviteCode);
            }
            reloadIfReturnedToMainMenu();
        }
    });

    // Sprint 30: Interfaz de Usuario para Emotes Sociales y Rueda Radial
    const emotesUI = new EmotesUI({
        onSendEmote: (emoteId: string) => {
            if (room) {
                room.send("use-emote", { emoteId });
            }
        },
        onOpenShop: () => {
            potionsUI.open();
            emotesUI.switchShopTab("emotes");
        }
    });

    const openPotionsBtn = document.getElementById("open-potions-btn");
    if (openPotionsBtn) {
        openPotionsBtn.addEventListener("click", () => {
            potionsUI.open();
        });
    }

    const aliasPotionsBadge = document.getElementById("alias-potions-badge");
    potionManager.subscribe((belt) => {
        if (aliasPotionsBadge) {
            const count = belt.filter((id) => typeof id === "string" && id.length > 0).length;
            aliasPotionsBadge.textContent = `${count}/3`;
        }
    });

    const stageElementSkinsBtn = document.getElementById("stage-element-skins-btn");
    if (stageElementSkinsBtn) {
        stageElementSkinsBtn.addEventListener("click", () => {
            elementSkinsModalManager.open();
        });
    }

    const stageFamiliarsBtn = document.getElementById("stage-familiars-btn");
    if (stageFamiliarsBtn) {
        stageFamiliarsBtn.addEventListener("click", () => {
            familiarsModalManager.open();
        });
    }

    // Salir de la arena al menú principal con guardado seguro
    document.getElementById("leave-btn")?.addEventListener("click", async () => {
        const result = await Swal.fire({
            title: '¿Salir de la arena?',
            text: 'Tu progreso de misiones y estadísticas se guardará automáticamente.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, Salir',
            cancelButtonText: 'Continuar jugando',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#38bdf8',
        });
        if (result.isConfirmed) {
            if (localSessionId && players.has(localSessionId)) {
                const localPlayer = players.get(localSessionId);
                if (localPlayer && localPlayer.alive && !localPlayer.isSpectator && !isSpectating) {
                    const currentLifeDuration = performance.now() - localPlayer.lifeStartTime;
                    if (!isPrivateRoom && !isTrialMode) {
                        questManager.trackSurvivalTime(currentLifeDuration);
                    }
                }
            }
            if (room) {
                try {
                    room.send("leave-arena");
                    await room.leave();
                } catch {
                    // Ignorar error si ya estaba desconectado
                }
            }
            if (!isPrivateRoom && !isTrialMode) {
                await questManager.flush();
                profileManager.save(true);
            }
            leaveToMainMenu();
        }
    });

    // Salir desde el HUD exclusivo de sala privada
    document.getElementById("private-hud-leave-btn")?.addEventListener("click", async () => {
        const result = await Swal.fire({
            title: '¿Salir de la sala privada?',
            text: 'Volverás al menú principal de Alchem.io.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, Salir',
            cancelButtonText: 'Continuar jugando',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#38bdf8',
        });
        if (result.isConfirmed) {
            if (room) {
                try {
                    room.send("leave-arena");
                    await room.leave();
                } catch {
                    // ignore
                }
            }
            leaveToMainMenu();
        }
    });

    // Salir desde el modal de victoria de sala privada
    document.getElementById("private-victory-leave-btn")?.addEventListener("click", async () => {
        if (room) {
            try {
                await room.leave();
            } catch {
                // ignore
            }
        }
        leaveToMainMenu();
    });

    // ==== Sprint 18.4: Arrancar el bucle visual ANTES de pedir el alias ====
    lastFrameTime = performance.now();
    requestAnimationFrame(gameLoop);

    const urlParams = new URLSearchParams(window.location.search);
    const trialIdParam = urlParams.get("trial");
    const partyCodeParam = urlParams.get("party");
    const botAllyParam = urlParams.get("botAlly") === "1" || urlParams.get("botAlly") === "true";
    const isValidTrialParam = Boolean(trialIdParam && trialIdParam.trim().length > 0 && trialIdParam.trim() !== "undefined");
    isTrialMode = isValidTrialParam;
    activeTrialId = isValidTrialParam ? trialIdParam!.trim() : null;
    activeTrialPartyCode = botAllyParam
        ? `BOT_ALLY_${Math.random().toString(36).slice(2, 8).toUpperCase()}`
        : partyCodeParam
        ? partyCodeParam.trim().toUpperCase()
        : null;

    if (isTrialMode) {
        const dims = getTrialArenaDimensions(activeTrialId);
        currentTrialArenaWidth = dims.width;
        currentTrialArenaHeight = dims.height;
        if ("setMapDimensions" in activeRenderer && typeof (activeRenderer as any).setMapDimensions === "function") {
            (activeRenderer as any).setMapDimensions(dims.width, dims.height);
        }
    }

    let alias: string;
    let startAsSpectator = false;
    let privateRoomCode: string | undefined;
    let privateBotCount: number | undefined;

    if (isTrialMode) {
        alias = sanitizeAlias(safeGetStorage("alchem_alias") || "") || generateAnonAlias();
        startAsSpectator = false;
        document.body.classList.remove("in-menu");
        document.getElementById("alias-modal")?.classList.add("hidden");
        document.getElementById("welcome-bonus-banner")?.classList.add("hidden");
        document.getElementById("hud")?.classList.add("hidden");
        document.getElementById("spawn-tooltip")?.classList.add("hidden");
        document.getElementById("shop-entry-btn")?.classList.add("hidden");
        document.getElementById("open-roulette-btn")?.classList.add("hidden");
    } else {
        const menuSelection = await menu.show(isInstantEntry);
        alias = menuSelection.alias;
        startAsSpectator = menuSelection.startAsSpectator;
        privateRoomCode = menuSelection.privateRoomCode;
        privateBotCount = menuSelection.privateBotCount;
    }

    finalRoomCode = privateRoomCode || activeInviteCode;
    isPrivateRoom = Boolean(typeof finalRoomCode === "string" && finalRoomCode.startsWith("ALCH-"));
    if (isPrivateRoom || isTrialMode) {
        if (isPrivateRoom) document.body.classList.add("is-private-room");
        document.getElementById("hud")?.classList.add("hidden");
    } else {
        document.body.classList.remove("is-private-room");
        document.getElementById("hud")?.classList.remove("hidden");
    }
    profileUI.setAlias(alias);

    function updateAudioDisplay(): void {
        const isMuted = audio.isMuted();
        const vol = audio.getVolume();
        if (audioIcon) {
            if (isMuted || vol === 0) {
                audioIcon.textContent = "🔇";
                audioIcon.classList.add("muted");
            } else if (vol < 0.4) {
                audioIcon.textContent = "🔉";
                audioIcon.classList.remove("muted");
            } else {
                audioIcon.textContent = "🔊";
                audioIcon.classList.remove("muted");
            }
        }
        if (volumeValueText) {
            volumeValueText.textContent = isMuted ? "0%" : `${Math.round(vol * 100)}%`;
        }
        if (volumeSlider && !isMuted) {
            volumeSlider.value = String(Math.round(vol * 100));
        }
        const privateHudMuteBtn = document.getElementById("private-hud-mute-btn");
        if (privateHudMuteBtn) {
            privateHudMuteBtn.textContent = isMuted ? "🔇" : "🔊";
        }
    }

    function toggleAudio(): void {
        audio.toggleMute();
        updateAudioDisplay();
    }

    // Inicializar slider de volumen interactivo
    if (volumeSlider) {
        volumeSlider.value = String(Math.round(audio.getVolume() * 100));
        volumeSlider.addEventListener("input", (e) => {
            const val = Number((e.target as HTMLInputElement).value);
            audio.setVolume(val / 100);
            if (val === 0) {
                if (!audio.isMuted()) audio.toggleMute();
            } else if (audio.isMuted()) {
                audio.toggleMute();
            }
            updateAudioDisplay();
        });
    }

    let volumeCloseTimer: ReturnType<typeof setTimeout> | null = null;

    if (audioGroup && volumePopover) {
        audioGroup.addEventListener("mouseenter", () => {
            if (volumeCloseTimer) {
                clearTimeout(volumeCloseTimer);
                volumeCloseTimer = null;
            }
            volumePopover.classList.remove("hidden");
        });

        audioGroup.addEventListener("mouseleave", () => {
            volumeCloseTimer = setTimeout(() => {
                volumePopover.classList.add("hidden");
            }, 300);
        });

        // Soporte táctil y click para móviles y PC
        audioIcon?.addEventListener("click", (e) => {
            e.stopPropagation();
            toggleAudio();
        });
    }

    updateAudioDisplay();

    // Toast flotante anticolisión de misiones cumplidas
    function showQuestCompletedToast(quest: any): void {
        const container = document.getElementById("quest-toast-container");
        if (!container) return;

        const toast = document.createElement("div");
        toast.className = "quest-toast";

        const rewards: string[] = [];
        if (quest.rewardCrystals && quest.rewardCrystals > 0) {
            rewards.push(`+${quest.rewardCrystals} 💎`);
        }
        if (quest.rewardAstralOrbs && quest.rewardAstralOrbs > 0) {
            rewards.push(`+${quest.rewardAstralOrbs} 🔮`);
        }
        const rewardText = rewards.length > 0 ? rewards.join("  ") : "¡Completada!";

        toast.innerHTML = `
            <span class="quest-toast-icon">🎯</span>
            <div class="quest-toast-body">
                <span class="quest-toast-title">🎯 ¡MISIÓN CUMPLIDA!</span>
                <span class="quest-toast-name">${quest.title || "Misión"}</span>
                <span class="quest-toast-reward">${rewardText}</span>
            </div>
        `;

        container.appendChild(toast);

        setTimeout(() => {
            toast.classList.add("fade-out");
            setTimeout(() => {
                toast.remove();
            }, 500);
        }, 4500);
    }

    questManager.onQuestCompleted = (quest) => {
        showQuestCompletedToast(quest);
        audio.playSuccessSfx?.();
    };

    function toggleHelpPanel(): void {
        if (helpPanel) helpPanel.classList.toggle("hidden");
    }

    function isModalOrInputFocused(): boolean {
        const activeEl = document.activeElement;
        if (activeEl && (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA")) {
            return true;
        }
        if (potionsUI.isOpen()) return true;
        const accountModal = document.getElementById("account-modal");
        if (accountModal && !accountModal.classList.contains("hidden")) return true;
        const profileModal = document.getElementById("profile-modal");
        if (profileModal && !profileModal.classList.contains("hidden")) return true;
        const questsModal = document.getElementById("quests-modal");
        if (questsModal && !questsModal.classList.contains("hidden")) return true;
        const aliasModal = document.getElementById("alias-modal");
        if (aliasModal && !aliasModal.classList.contains("hidden")) return true;
        return false;
    }

    function closeAllModals(): void {
        accountManager.close();
        profileUI.close();
        questsUI.close();
        potionsUI.close();
        if (helpPanel) helpPanel.classList.add("hidden");
        if (localSessionId && players.has(localSessionId) && players.get(localSessionId)!.alive && !isSpectating) {
            showCrazyGamesInviteButton(activeInviteCode);
        }
    }

    function cycleSkin(direction: number): void {
        menu.cycleSkin(direction);
        currentCosmetics = menu.getCosmetics();
    }

    if (helpToggle) helpToggle.addEventListener("click", toggleHelpPanel);
    if (audioToggle) audioToggle.addEventListener("click", toggleAudio);
    const privateHudMuteBtn = document.getElementById("private-hud-mute-btn");
    if (privateHudMuteBtn) privateHudMuteBtn.addEventListener("click", toggleAudio);

    const inviteBtn = document.getElementById("invite-btn");
    const inviteBtnText = document.getElementById("invite-btn-text");
    let inviteResetTimer: ReturnType<typeof setTimeout> | null = null;
    if (inviteBtn) {
        inviteBtn.addEventListener("click", async () => {
            const link = createCrazyGamesInviteLink(activeInviteCode);
            showCrazyGamesInviteButton(activeInviteCode);
            if (link) {
                try {
                    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
                        await navigator.clipboard.writeText(link);
                    } else {
                        throw new Error("Clipboard API not available");
                    }
                } catch {
                    try {
                        const tempInput = document.createElement("input");
                        tempInput.value = link;
                        tempInput.style.position = "fixed";
                        tempInput.style.opacity = "0";
                        document.body.appendChild(tempInput);
                        tempInput.focus();
                        tempInput.select();
                        document.execCommand("copy");
                        document.body.removeChild(tempInput);
                    } catch (e) {
                        console.warn("[Invite] Error al copiar enlace al portapapeles:", e);
                    }
                }
            }
            if (inviteBtnText) {
                const originalText = inviteBtnText.textContent;
                inviteBtnText.textContent = "✅ COPIADO";
                if (inviteResetTimer) clearTimeout(inviteResetTimer);
                inviteResetTimer = setTimeout(() => {
                    inviteBtnText.textContent = originalText || "🔗 INVITAR";
                }, 2000);
            }
        });
    }





    window.addEventListener("pointermove", () => {
        lastMouseMoveTime = performance.now();
    });


    // 1. Conexión con el servidor autoritativo
    try {
        const authToken = localStorage.getItem("alchem_auth_token") || localStorage.getItem("colyseus-auth-token") || undefined;
        if (isTrialMode && activeTrialId) {
            currentCosmetics = menu.getCosmetics();
            const isCoopFlag = Boolean(activeTrialPartyCode || activeTrialId === "trial_coop_purge");
            const skinPayload = botAllyParam
                ? JSON.stringify({ ...currentCosmetics, botAlly: 1 })
                : JSON.stringify(currentCosmetics);
            room = await connectToTrialRoom(
                activeTrialId,
                activeTrialPartyCode || undefined,
                isCoopFlag,
                authToken,
                alias,
                (profileManager.getProfile() as { userId?: string }).userId || undefined,
                skinPayload
            );
        } else {
            room = await connectToServer(alias, false, finalRoomCode, authToken, privateBotCount);
        }
        menu.setRoom(room);
        familiarsModalManager.setRoom(room);
        elementSkinsModalManager.setRoom(room);
        if (!isTrialMode) {
            try {
                window.history.replaceState({}, document.title, window.location.pathname);
            } catch {
                // ignore
            }
        }
    } catch (err) {
        if (connectionStatus) {
            connectionStatus.textContent = t("misc.connectionError");
        }
        return;
    }
    attractModeActive = false;

    const shareableRoomId = finalRoomCode === "public" ? finalRoomCode : finalRoomCode;

    localSessionId = room.sessionId || getLocalSessionId(room);
    if (overlay) {
        overlay.classList.add("hidden");
    }
    document.body.classList.remove("in-menu");
    canvas.style.pointerEvents = "auto";
    try {
        currentCosmetics = menu.getCosmetics();
        room.send("set-skin", { skin: JSON.stringify(currentCosmetics) });
    } catch {
        // network send failure ignore
    }

    // Sprint 33: Barra HUD Único y Eventos de Sala Privada
    const privateHudBar = document.getElementById("private-room-hud-bar");
    const privateHudCode = document.getElementById("private-hud-code");
    const privateHudPlayers = document.getElementById("private-hud-players");
    const privateHudCopyBtn = document.getElementById("private-hud-copy-btn");
    const privateHudStartBtn = document.getElementById("private-hud-start-btn");
    const privateHudTimer = document.getElementById("private-hud-timer");
    const privateHudGoal = document.getElementById("private-hud-goal");
    let hasShownWarmupToast = false;
    let victoryModalTimer: ReturnType<typeof setTimeout> | null = null;

    if (privateHudStartBtn) {
        privateHudStartBtn.classList.add("hidden");
        privateHudStartBtn.style.display = "none";
    }

    updatePrivateHudCount = (): void => {
        let count = 0;
        for (const p of players.values()) {
            if (!p.isSpectator) count++;
        }
        if (privateHudPlayers) {
            privateHudPlayers.textContent = `👥 ${count}/10`;
        }
    };

    if (isPrivateRoom) {
        // En salas privadas ocultamos el HUD público y mostramos el HUD exclusivo
        document.body.classList.add("is-private-room");
        document.getElementById("hud")?.classList.add("hidden");
        if (privateHudBar) {
            privateHudBar.classList.remove("hidden");
        }
        if (privateHudCode) {
            privateHudCode.textContent = `🔒 ${finalRoomCode}`;
        }
        if (privateHudStartBtn) {
            privateHudStartBtn.classList.add("hidden");
            privateHudStartBtn.style.display = "none";
        }
        updatePrivateHudCount();

        // Enviar configuración seleccionada por el Host al servidor
        const durVal = Number((document.getElementById("private-duration-val") as HTMLInputElement)?.value) || 3;
        const gType = (document.getElementById("private-goal-type-select") as HTMLSelectElement)?.value || "time";
        const gKills = Number((document.getElementById("private-goal-kills-select") as HTMLSelectElement)?.value) || 10;
        const gMass = Number((document.getElementById("private-goal-mass-select") as HTMLSelectElement)?.value) || 5000;
        try {
            room.send("configure-private-room", {
                durationMinutes: durVal,
                goalType: gType,
                goalKills: gKills,
                goalMass: gMass,
            });
        } catch {
            // ignore
        }

        if (privateHudCopyBtn) {
            let copyTimer: ReturnType<typeof setTimeout> | null = null;
            privateHudCopyBtn.addEventListener("click", async () => {
                const link = `${window.location.origin}${window.location.pathname}?roomId=${finalRoomCode}`;
                try {
                    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
                        await navigator.clipboard.writeText(link);
                    } else {
                        throw new Error("Clipboard API not available");
                    }
                } catch {
                    const tempInput = document.createElement("input");
                    tempInput.value = link;
                    tempInput.style.position = "fixed";
                    tempInput.style.opacity = "0";
                    document.body.appendChild(tempInput);
                    tempInput.focus();
                    tempInput.select();
                    document.execCommand("copy");
                    document.body.removeChild(tempInput);
                }
                privateHudCopyBtn.textContent = "✅ Copiado";
                if (copyTimer) clearTimeout(copyTimer);
                copyTimer = setTimeout(() => {
                    privateHudCopyBtn.textContent = "📋 Invitar";
                }, 2000);
            });
        }

        if (privateHudStartBtn) {
            privateHudStartBtn.addEventListener("click", () => {
                if (victoryModalTimer) {
                    clearTimeout(victoryModalTimer);
                    victoryModalTimer = null;
                }
                document.getElementById("private-victory-modal")?.classList.add("hidden");
                room.send("start-private-match");
            });
        }

        document.getElementById("private-victory-rematch-btn")?.addEventListener("click", () => {
            if (victoryModalTimer) {
                clearTimeout(victoryModalTimer);
                victoryModalTimer = null;
            }
            document.getElementById("private-victory-modal")?.classList.add("hidden");
            room.send("start-private-match");
        });

        room.onMessage("private-room-role", (data: { isHost?: boolean }) => {
            if (privateHudStartBtn) {
                if (data?.isHost) {
                    privateHudStartBtn.classList.remove("hidden");
                    privateHudStartBtn.style.display = "inline-flex";
                } else {
                    privateHudStartBtn.classList.add("hidden");
                    privateHudStartBtn.style.display = "none";
                }
            }
        });

        room.onMessage("private-room-info", (info: {
            isPrivate: boolean;
            roomCode: string;
            hostSessionId: string | null;
            configuredBots: number;
            maxClients: number;
            matchPhase: "warmup" | "countdown" | "active" | "ended";
            durationMinutes: number;
            remainingSeconds: number;
            goalType: "time" | "kills" | "mass" | "both";
            goalKills: number;
            goalMass: number;
        }) => {
            if (!info || !info.isPrivate) return;

            // Garantizar ocultación estricta del HUD público
            document.getElementById("hud")?.classList.add("hidden");
            privateHudBar?.classList.remove("hidden");

            // Modal vistoso SweetAlert2 de 3s en modo prueba (calentamiento)
            if (!hasShownWarmupToast && info.matchPhase === "warmup") {
                hasShownWarmupToast = true;
                void Swal.fire({
                    title: "🧪 MODO PRUEBA ACTIVO",
                    html: "Estás jugando en <b>Modo Prueba (Calentamiento)</b> mientras se unen tus amigos.<br><br>Cuando todos estén listos, el <b>Anfitrión</b> iniciará la batalla oficial.",
                    icon: "info",
                    timer: 3000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    background: "rgba(15, 23, 42, 0.96)",
                    color: "#f8fafc",
                    backdrop: "rgba(2, 6, 23, 0.6)",
                });
            }

            if (privateHudCode) {
                privateHudCode.textContent = `🔒 ${info.roomCode}`;
            }

            const mySessionId = localSessionId || room.sessionId;
            const isHost = Boolean(mySessionId && info.hostSessionId && mySessionId === info.hostSessionId);
            if (privateHudStartBtn) {
                if (isHost) {
                    privateHudStartBtn.classList.remove("hidden");
                    privateHudStartBtn.style.display = "inline-flex";
                    if (info.matchPhase === "warmup") {
                        privateHudStartBtn.textContent = "🚀 Iniciar";
                    } else if (info.matchPhase === "active") {
                        privateHudStartBtn.textContent = "🔄 Reiniciar";
                    } else if (info.matchPhase === "ended") {
                        privateHudStartBtn.textContent = "🚀 Nueva";
                    }
                } else {
                    privateHudStartBtn.classList.add("hidden");
                    privateHudStartBtn.style.display = "none";
                }
            }

            if (privateHudTimer) {
                if (info.matchPhase === "warmup") {
                    privateHudTimer.textContent = "🧪 PRUEBA";
                    privateHudTimer.style.color = "#38bdf8";
                } else if (info.matchPhase === "countdown") {
                    privateHudTimer.textContent = "⚔️ 3..2..1";
                    privateHudTimer.style.color = "#f59e0b";
                } else if (info.matchPhase === "active") {
                    const mins = Math.floor(info.remainingSeconds / 60);
                    const secs = info.remainingSeconds % 60;
                    privateHudTimer.textContent = `⏱️ ${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
                    privateHudTimer.style.color = info.remainingSeconds <= 30 ? "#ef4444" : "#38bdf8";
                } else if (info.matchPhase === "ended") {
                    privateHudTimer.textContent = "🏁 FIN";
                    privateHudTimer.style.color = "#fbbf24";
                }
            }

            if (privateHudGoal) {
                if (info.goalType === "kills") {
                    privateHudGoal.textContent = `🏆 ${info.goalKills} Kills`;
                } else if (info.goalType === "mass") {
                    privateHudGoal.textContent = `🏆 ${info.goalMass.toLocaleString()} Masa`;
                } else if (info.goalType === "both") {
                    privateHudGoal.textContent = `🏆 ${info.goalKills}K + ${info.goalMass.toLocaleString()}M`;
                } else {
                    privateHudGoal.textContent = "🏆 Tiempo";
                }
            }
        });

        room.onMessage("private-match-countdown", (data: { seconds: number }) => {
            if (victoryModalTimer) {
                clearTimeout(victoryModalTimer);
                victoryModalTimer = null;
            }
            if (typeof Swal !== "undefined" && Swal.isVisible()) {
                Swal.close();
            }
            document.getElementById("private-victory-modal")?.classList.add("hidden");
            document.getElementById("private-warmup-toast")?.classList.add("hidden");

            const overlay = document.getElementById("private-countdown-overlay");
            const titleEl = document.getElementById("private-countdown-title");
            const numEl = document.getElementById("private-countdown-number");

            if (overlay) overlay.classList.remove("hidden");

            if (data.seconds > 0) {
                if (titleEl) titleEl.textContent = "⚔️ LA BATALLA COMIENZA EN";
                if (numEl) {
                    numEl.textContent = String(data.seconds);
                    numEl.style.fontSize = "";
                    numEl.style.color = "";
                    numEl.style.animation = "none";
                    void numEl.offsetWidth;
                    numEl.style.animation = "countdownPop 0.9s cubic-bezier(0.175, 0.885, 0.32, 1.275)";
                }
                try {
                    audio.playPowerUpSfx?.();
                } catch {
                    // ignore
                }
            } else {
                if (titleEl) titleEl.textContent = "🔥 ¡PREPARADOS!";
                if (numEl) {
                    numEl.textContent = "⚔️ ¡A BATALLAR!";
                    numEl.style.fontSize = "52px";
                    numEl.style.color = "";
                    numEl.style.animation = "none";
                    void numEl.offsetWidth;
                    numEl.style.animation = "countdownPop 0.9s cubic-bezier(0.175, 0.885, 0.32, 1.275)";
                }
                try {
                    audio.playSuccessSfx?.();
                } catch {
                    // ignore
                }
                setTimeout(() => {
                    overlay?.classList.add("hidden");
                    if (numEl) {
                        numEl.style.fontSize = "";
                        numEl.style.color = "";
                    }
                }, 900);
            }
        });

        const drawChampionAvatar = (winner: { element?: string; name?: string; skinId?: string } | null): void => {
            const canvas = document.getElementById("private-winner-canvas") as HTMLCanvasElement | null;
            if (!canvas) return;
            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const cx = canvas.width / 2;
            const cy = canvas.height / 2;
            const r = 38;

            const el = winner?.element || "fire";
            const color = ELEMENT_COLORS[el] || "#38bdf8";

            // Halo dorado radiante exterior
            const goldAura = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, r * 1.45);
            goldAura.addColorStop(0, "rgba(251, 191, 36, 0.45)");
            goldAura.addColorStop(0.6, "rgba(245, 158, 11, 0.25)");
            goldAura.addColorStop(1, "transparent");
            ctx.fillStyle = goldAura;
            ctx.beginPath();
            ctx.arc(cx, cy, r * 1.45, 0, Math.PI * 2);
            ctx.fill();

            // Partículas / estrellas decorativas doradas alrededor
            const stars = [
                { angle: -0.6, dist: r * 1.25, size: 4 },
                { angle: 0.7, dist: r * 1.3, size: 3.5 },
                { angle: 2.2, dist: r * 1.2, size: 4.5 },
                { angle: 3.6, dist: r * 1.28, size: 3 },
                { angle: -2.1, dist: r * 1.22, size: 4 },
            ];
            ctx.fillStyle = "#fef08a";
            stars.forEach((s) => {
                const sx = cx + Math.cos(s.angle) * s.dist;
                const sy = cy + Math.sin(s.angle) * s.dist;
                ctx.beginPath();
                ctx.arc(sx, sy, s.size, 0, Math.PI * 2);
                ctx.fill();
            });

            // Aura de color elemental
            const glow = ctx.createRadialGradient(cx, cy, r * 0.4, cx, cy, r * 1.15);
            glow.addColorStop(0, color);
            glow.addColorStop(0.8, color + "55");
            glow.addColorStop(1, "transparent");
            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.arc(cx, cy, r * 1.15, 0, Math.PI * 2);
            ctx.fill();

            // Esfera elemental
            const sphereGrad = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.35, r * 0.1, cx, cy, r);
            sphereGrad.addColorStop(0, "#ffffff");
            sphereGrad.addColorStop(0.35, color);
            sphereGrad.addColorStop(1, "#090d16");
            ctx.fillStyle = sphereGrad;
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.fill();

            // Borde dorado grueso de campeón
            ctx.strokeStyle = "#fbbf24";
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.stroke();

            // Dibujar ojos y sonrisa estilo slime/avatar de Alchem.io
            ctx.fillStyle = "#ffffff";
            // Ojo izquierdo
            ctx.beginPath();
            ctx.arc(cx - 10, cy - 6, 6, 0, Math.PI * 2);
            ctx.fill();
            // Ojo derecho
            ctx.beginPath();
            ctx.arc(cx + 10, cy - 6, 6, 0, Math.PI * 2);
            ctx.fill();

            // Pupilas negras con brillo
            ctx.fillStyle = "#0f172a";
            ctx.beginPath();
            ctx.arc(cx - 9, cy - 6, 3, 0, Math.PI * 2);
            ctx.arc(cx + 11, cy - 6, 3, 0, Math.PI * 2);
            ctx.fill();

            ctx.fillStyle = "#ffffff";
            ctx.beginPath();
            ctx.arc(cx - 10, cy - 7, 1.2, 0, Math.PI * 2);
            ctx.arc(cx + 10, cy - 7, 1.2, 0, Math.PI * 2);
            ctx.fill();

            // Sonrisa alegre de campeón
            ctx.strokeStyle = "#0f172a";
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.arc(cx, cy + 5, 10, 0.2 * Math.PI, 0.8 * Math.PI, false);
            ctx.stroke();

            // Pequeño icono elemental encima de la esfera
            const elEmojis: Record<string, string> = {
                fire: "🔥",
                water: "💧",
                plant: "🌿",
                electric: "⚡",
                earth: "🪨",
            };
            const icon = elEmojis[el] || "👑";
            ctx.font = "16px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(icon, cx, cy - r + 4);
        };

        room.onMessage("private-match-ended", (data: {
            winner: { sessionId: string; name: string; element: string; skinId: string; kills: number; deaths: number; mass: number; isBot: boolean } | null;
            reason: string;
            rankings: Array<{ sessionId: string; name: string; element: string; skinId: string; kills: number; deaths: number; mass: number; isBot: boolean }>;
            hostSessionId: string | null;
        }) => {
            if (victoryModalTimer) {
                clearTimeout(victoryModalTimer);
                victoryModalTimer = null;
            }

            // Paso A (Alerta Inmediata de 2.2 segundos)
            try {
                audio.playSuccessSfx?.();
            } catch {
                // ignore
            }

            const overlay = document.getElementById("private-countdown-overlay");
            const titleEl = document.getElementById("private-countdown-title");
            const numEl = document.getElementById("private-countdown-number");

            if (overlay) overlay.classList.remove("hidden");
            if (titleEl) {
                titleEl.textContent = `🏆 ${data.reason || "¡META ALCANZADA!"}`;
            }
            if (numEl) {
                numEl.textContent = "🏁 ¡PARTIDA FINALIZADA!";
                numEl.style.fontSize = "44px";
                numEl.style.color = "#fbbf24";
                numEl.style.animation = "none";
                void numEl.offsetWidth;
                numEl.style.animation = "countdownPop 0.9s cubic-bezier(0.175, 0.885, 0.32, 1.275)";
            }

            // Preparar datos del Campeón y Ranking en el modal
            const modal = document.getElementById("private-victory-modal");
            const reasonEl = document.getElementById("private-victory-reason");
            if (reasonEl) reasonEl.textContent = data.reason || "¡Fin de la partida!";

            const winner = data.winner;
            const winnerNameEl = document.getElementById("private-winner-name");
            if (winnerNameEl) winnerNameEl.textContent = winner ? `${winner.name}${winner.isBot ? " (Bot)" : ""}` : "Nadie";

            const winnerStatsEl = document.getElementById("private-winner-stats");
            if (winnerStatsEl && winner) {
                winnerStatsEl.textContent = `⚪ ${winner.mass.toLocaleString()} Masa • ⚔️ ${winner.kills} Kills • 💀 ${winner.deaths} Muertes`;
            }

            drawChampionAvatar(winner);

            // Tabla de posiciones
            const rankingBody = document.getElementById("private-victory-ranking-list");
            if (rankingBody) {
                rankingBody.innerHTML = "";
                const mySessionId = localSessionId || room.sessionId;
                (data.rankings || []).forEach((p, idx) => {
                    const tr = document.createElement("tr");
                    if (idx === 0) {
                        tr.classList.add("champion-row");
                    }
                    let posDisplay = `#${idx + 1}`;
                    let posClass = "private-rank-pos";
                    if (idx === 0) {
                        posDisplay = "🥇 #1";
                        posClass += " gold";
                    } else if (idx === 1) {
                        posDisplay = "🥈 #2";
                        posClass += " silver";
                    } else if (idx === 2) {
                        posDisplay = "🥉 #3";
                        posClass += " bronze";
                    }

                    const isLocal = Boolean(mySessionId && p.sessionId === mySessionId);

                    const tdPos = document.createElement("td");
                    tdPos.className = posClass;
                    tdPos.textContent = posDisplay;

                    const tdName = document.createElement("td");
                    tdName.style.fontWeight = "700";
                    tdName.style.color = "#f8fafc";
                    tdName.textContent = p.name || "";

                    if (isLocal) {
                        const youSpan = document.createElement("span");
                        youSpan.style.cssText = "font-size: 10px; font-weight: 800; color: #38bdf8; background: rgba(56, 189, 248, 0.2); padding: 1px 5px; border-radius: 4px; border: 1px solid rgba(56, 189, 248, 0.4); margin-left: 4px;";
                        youSpan.textContent = "(TÚ)";
                        tdName.appendChild(youSpan);
                    }

                    if (p.isBot) {
                        const botSpan = document.createElement("span");
                        botSpan.style.cssText = "font-size: 10px; color: #94a3b8; margin-left: 4px;";
                        botSpan.textContent = "(Bot)";
                        tdName.appendChild(botSpan);
                    }

                    const tdKills = document.createElement("td");
                    tdKills.style.color = "#38bdf8";
                    tdKills.style.fontWeight = "700";
                    tdKills.textContent = String(p.kills ?? 0);

                    const tdDeaths = document.createElement("td");
                    tdDeaths.style.color = "#f87171";
                    tdDeaths.textContent = String(p.deaths ?? 0);

                    const tdMass = document.createElement("td");
                    tdMass.style.color = "#fbbf24";
                    tdMass.style.fontWeight = "700";
                    tdMass.textContent = Number(p.mass || 0).toLocaleString();

                    tr.appendChild(tdPos);
                    tr.appendChild(tdName);
                    tr.appendChild(tdKills);
                    tr.appendChild(tdDeaths);
                    tr.appendChild(tdMass);

                    rankingBody.appendChild(tr);
                });
            }

            const mySessionId = localSessionId || room.sessionId;
            const isHost = Boolean(mySessionId && data.hostSessionId && mySessionId === data.hostSessionId);
            const rematchBtn = document.getElementById("private-victory-rematch-btn");
            const waitHost = document.getElementById("private-victory-wait-host");

            if (rematchBtn) {
                if (isHost) {
                    rematchBtn.classList.remove("hidden");
                    rematchBtn.style.display = "inline-flex";
                } else {
                    rematchBtn.classList.add("hidden");
                    rematchBtn.style.display = "none";
                }
            }
            if (waitHost) {
                if (isHost) {
                    waitHost.classList.add("hidden");
                    waitHost.style.display = "none";
                } else {
                    waitHost.classList.remove("hidden");
                    waitHost.style.display = "block";
                }
            }

            // Paso B (Despliegue del Modal Llamativo tras 2200ms)
            victoryModalTimer = setTimeout(() => {
                victoryModalTimer = null;
                overlay?.classList.add("hidden");
                if (numEl) {
                    numEl.style.fontSize = "";
                    numEl.style.color = "";
                }
                modal?.classList.remove("hidden");
            }, 2200);
        });
    }

    // ==== Sistema de Tareas Diarias y Arenas de Desafío (TrialRoom HUD & Modales) ====
    if (isTrialMode) {
        document.getElementById("hud")?.classList.add("hidden");
        document.getElementById("private-room-hud-bar")?.classList.add("hidden");

        // Inyectar #trial-hud en la parte superior central
        let trialHud = document.getElementById("trial-hud");
        if (!trialHud) {
            trialHud = document.createElement("div");
            trialHud.id = "trial-hud";
            trialHud.style.cssText = `
                position: fixed;
                top: 12px;
                left: 50%;
                transform: translateX(-50%);
                z-index: 1200;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: center;
                gap: 12px;
                padding: 10px 18px;
                background: rgba(9, 13, 22, 0.88);
                border: 1px solid rgba(56, 189, 248, 0.45);
                border-radius: 16px;
                box-shadow: 0 12px 32px rgba(0, 0, 0, 0.65), 0 0 20px rgba(56, 189, 248, 0.18);
                backdrop-filter: blur(14px);
                color: #f8fafc;
                font-family: 'Plus Jakarta Sans', sans-serif;
                max-width: 95vw;
            `;
            trialHud.innerHTML = `
                <div id="trial-hud-title" style="font-weight: 800; font-size: 0.9rem; color: #38bdf8; display: flex; align-items: center; gap: 6px;">
                    <span>⚔️</span> <span id="trial-hud-title-text">Arena de Desafío</span>
                </div>
                <div style="width: 1px; height: 20px; background: rgba(148, 163, 184, 0.25);"></div>
                <div id="trial-hud-timer" style="font-weight: 900; font-size: 1.05rem; color: #fde047; letter-spacing: 0.03em;">
                    ⏳ Tiempo: 02:00
                </div>
                <div style="width: 1px; height: 20px; background: rgba(148, 163, 184, 0.25);"></div>
                <div id="trial-hud-goal" style="font-weight: 800; font-size: 0.9rem; color: #e2e8f0;">
                    🎯 Meta: 0 / 1
                </div>
                <div id="trial-hud-ally-sep" style="width: 1px; height: 20px; background: rgba(148, 163, 184, 0.25); display: none;"></div>
                <div id="trial-hud-ally" style="font-weight: 700; font-size: 0.85rem; color: #c084fc; display: none;">
                    🤝 Compañero: Esperando...
                </div>
                <button id="trial-hud-exit-btn" type="button" style="
                    margin-left: 6px;
                    padding: 6px 12px;
                    border-radius: 10px;
                    border: 1px solid rgba(239, 68, 68, 0.5);
                    background: rgba(239, 68, 68, 0.18);
                    color: #fca5a5;
                    font-weight: 800;
                    font-size: 0.78rem;
                    cursor: pointer;
                ">🚪 Salir</button>
            `;
            document.body.appendChild(trialHud);

            document.getElementById("trial-hud-exit-btn")?.addEventListener("click", () => {
                document.documentElement.classList.remove("trial-booting");
                window.location.href = "/perfil.html";
            });
        }

        // Overlay de espera de compañero cooperativo y cuenta regresiva
        let trialWaitingOverlay = document.getElementById("trial-waiting-overlay");
        if (!trialWaitingOverlay) {
            trialWaitingOverlay = document.createElement("div");
            trialWaitingOverlay.id = "trial-waiting-overlay";
            trialWaitingOverlay.className = "hidden";
            trialWaitingOverlay.style.cssText = `
                position: fixed;
                inset: 0;
                z-index: 1800;
                display: none;
                align-items: center;
                justify-content: center;
                background: rgba(5, 8, 17, 0.78);
                backdrop-filter: blur(8px);
                padding: 16px;
            `;
            trialWaitingOverlay.style.display = "none";
            trialWaitingOverlay.innerHTML = `
                <div style="
                    max-width: 440px;
                    width: 100%;
                    padding: 28px 24px;
                    border-radius: 20px;
                    background: rgba(15, 23, 42, 0.95);
                    border: 1px solid rgba(192, 132, 252, 0.55);
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8), 0 0 30px rgba(168, 85, 247, 0.25);
                    text-align: center;
                    color: #f8fafc;
                    font-family: 'Plus Jakarta Sans', sans-serif;
                ">
                    <div style="font-size: 2.4rem; margin-bottom: 8px;">🤝</div>
                    <h3 id="trial-wait-title" style="margin: 0 0 8px; font-size: 1.25rem; font-weight: 800; color: #e9d5ff;">
                        Esperando a tu Compañero Alquimista...
                    </h3>
                    <p id="trial-wait-desc" style="margin: 0 0 16px; font-size: 0.88rem; color: #cbd5e1;">
                        Comparte el código o enlace directo para que tu aliado se una a la arena cooperativa sin fuego amigo:
                    </p>
                    <div id="trial-wait-code" style="
                        font-family: monospace;
                        font-size: 1.35rem;
                        font-weight: 900;
                        letter-spacing: 0.12em;
                        color: #fde047;
                        background: rgba(30, 27, 75, 0.9);
                        border: 1px solid rgba(251, 191, 36, 0.45);
                        border-radius: 12px;
                        padding: 10px 16px;
                        margin-bottom: 14px;
                    ">${activeTrialPartyCode || "COOP"}</div>
                    <div style="display: flex; gap: 10px; justify-content: center;">
                        <button id="trial-wait-copy-btn" type="button" style="
                            padding: 10px 16px;
                            border-radius: 10px;
                            border: 1px solid rgba(56, 189, 248, 0.5);
                            background: linear-gradient(135deg, #0284c7, #2563eb);
                            color: #ffffff;
                            font-weight: 800;
                            cursor: pointer;
                        ">📋 Copiar Enlace Cooperativo</button>
                        <button id="trial-wait-cancel-btn" type="button" style="
                            padding: 10px 16px;
                            border-radius: 10px;
                            border: 1px solid rgba(148, 163, 184, 0.4);
                            background: rgba(30, 41, 59, 0.9);
                            color: #cbd5e1;
                            font-weight: 700;
                            cursor: pointer;
                        ">Salir al Perfil</button>
                    </div>
                </div>
            `;
            document.body.appendChild(trialWaitingOverlay);

            document.getElementById("trial-wait-copy-btn")?.addEventListener("click", async () => {
                const copyBtn = document.getElementById("trial-wait-copy-btn");
                const shareUrl = `${window.location.origin}/?trial=${encodeURIComponent(activeTrialId || "trial_coop_purge")}&party=${encodeURIComponent(activeTrialPartyCode || "")}`;
                try {
                    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
                        await navigator.clipboard.writeText(shareUrl);
                    }
                    if (copyBtn) copyBtn.textContent = "✅ ¡Enlace Copiado!";
                } catch {
                    // ignore
                }
            });

            document.getElementById("trial-wait-cancel-btn")?.addEventListener("click", () => {
                document.documentElement.classList.remove("trial-booting");
                window.location.href = "/perfil.html";
            });
        }

        // Modal de resultado de Victoria / Derrota en TrialRoom
        let trialResultModal = document.getElementById("trial-result-modal");
        if (!trialResultModal) {
            trialResultModal = document.createElement("div");
            trialResultModal.id = "trial-result-modal";
            trialResultModal.className = "hidden";
            trialResultModal.style.cssText = `
                position: fixed;
                inset: 0;
                z-index: 2200;
                display: none;
                align-items: center;
                justify-content: center;
                background: rgba(5, 8, 17, 0.84);
                backdrop-filter: blur(12px);
                padding: 16px;
            `;
            trialResultModal.style.display = "none";
            document.body.appendChild(trialResultModal);
        }

        const formatMmSs = (sec: number): string => {
            const s = Math.max(0, Math.floor(sec));
            const m = Math.floor(s / 60);
            const r = s % 60;
            return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
        };

        const renderTrialHudState = (payload: {
            title?: string;
            icon?: string;
            mode?: string;
            isCoop?: number | boolean;
            partyCode?: string;
            status?: string;
            remainingSeconds?: number;
            remainingSec?: number;
            progress?: number;
            targetGoal?: number;
            ally?: { sessionId: string; alias: string; alive: boolean; mass: number } | null;
            allies?: Array<{ sessionId: string; alias: string; alive: boolean; mass: number; kills?: number }>;
        }): void => {
            const titleTextEl = document.getElementById("trial-hud-title-text");
            const timerEl = document.getElementById("trial-hud-timer");
            const goalEl = document.getElementById("trial-hud-goal");
            const allySepEl = document.getElementById("trial-hud-ally-sep");
            const allyEl = document.getElementById("trial-hud-ally");

            if (titleTextEl && payload.title) {
                titleTextEl.textContent = payload.title;
            }
            const remSeconds = typeof payload.remainingSeconds === "number"
                ? payload.remainingSeconds
                : typeof payload.remainingSec === "number"
                ? payload.remainingSec
                : undefined;
            if (timerEl && typeof remSeconds === "number") {
                timerEl.textContent = `⏳ Tiempo: ${formatMmSs(remSeconds)}`;
                timerEl.style.color = remSeconds <= 15 ? "#ef4444" : "#fde047";
            }
            if (goalEl && typeof payload.progress === "number" && typeof payload.targetGoal === "number") {
                const modeUnit = payload.mode === "survival" || payload.mode === "pacifist"
                    ? "s"
                    : payload.mode === "mass_rush"
                    ? " Masa"
                    : payload.mode === "speed_collector"
                    ? " Orbes"
                    : "";
                goalEl.textContent = `🎯 Meta: ${payload.progress} / ${payload.targetGoal}${modeUnit}`;
            }

            if (Number(payload.isCoop) === 1 || payload.isCoop === true) {
                if (allySepEl) allySepEl.style.display = "block";
                if (allyEl) {
                    allyEl.style.display = "block";
                    const myId = localSessionId || room.sessionId;
                    const partner = payload.ally || (payload.allies || []).find((a) => a.sessionId !== myId);
                    if (partner) {
                        const statusLabel = partner.alive ? "Vivo" : "Reapareciendo...";
                        allyEl.textContent = `🤝 Compañero: ${partner.alias} (${statusLabel})`;
                        allyEl.style.color = partner.alive ? "#86efac" : "#fde047";
                    } else {
                        allyEl.textContent = `🤝 Compañero: Esperando...`;
                        allyEl.style.color = "#c084fc";
                    }
                }
            }
        };

        room.onMessage("trial_init", (data: Record<string, unknown>) => {
            if (typeof data?.partyCode === "string" && data.partyCode) {
                activeTrialPartyCode = data.partyCode;
                const codeEl = document.getElementById("trial-wait-code");
                if (codeEl) codeEl.textContent = data.partyCode;
            }
            renderTrialHudState(data);
            if (data?.status === "WAITING_PARTNER") {
                if (trialWaitingOverlay) {
                    trialWaitingOverlay.style.display = "flex";
                    trialWaitingOverlay.classList.remove("hidden");
                }
            } else {
                if (trialWaitingOverlay) {
                    trialWaitingOverlay.style.display = "none";
                    trialWaitingOverlay.classList.add("hidden");
                }
            }
        });

        room.onMessage("waiting_partner", (data: { partyCode?: string; message?: string }) => {
            if (data?.partyCode) {
                activeTrialPartyCode = data.partyCode;
                const codeEl = document.getElementById("trial-wait-code");
                if (codeEl) codeEl.textContent = data.partyCode;
            }
            if (trialWaitingOverlay) {
                trialWaitingOverlay.style.display = "flex";
                trialWaitingOverlay.classList.remove("hidden");
            }
        });

        room.onMessage("trial_countdown", (data: { seconds: number }) => {
            if (data.seconds <= 0) {
                if (trialWaitingOverlay) {
                    trialWaitingOverlay.style.display = "none";
                    trialWaitingOverlay.classList.add("hidden");
                }
                return;
            }
            if (trialWaitingOverlay) {
                trialWaitingOverlay.style.display = "flex";
                trialWaitingOverlay.classList.remove("hidden");
            }
            const titleEl = document.getElementById("trial-wait-title");
            const descEl = document.getElementById("trial-wait-desc");
            const codeEl = document.getElementById("trial-wait-code");
            if (titleEl) titleEl.textContent = "⚔️ ¡Compañero Conectado!";
            if (descEl) descEl.textContent = "El desafío cooperativo comienza en:";
            if (codeEl) codeEl.textContent = `${data.seconds}...`;
            try {
                audio.playPowerUpSfx?.();
            } catch {
                // ignore
            }
        });

        room.onMessage("trial_start", (data?: { arenaWidth?: number; arenaHeight?: number }) => {
            const aw = Number(data?.arenaWidth) || (activeTrialId ? getTrialArenaDimensions(activeTrialId).width : 3000);
            const ah = Number(data?.arenaHeight) || (activeTrialId ? getTrialArenaDimensions(activeTrialId).height : 3000);
            currentTrialArenaWidth = aw;
            currentTrialArenaHeight = ah;
            if ("setMapDimensions" in activeRenderer && typeof (activeRenderer as any).setMapDimensions === "function") {
                (activeRenderer as any).setMapDimensions(aw, ah);
            }

            if (trialWaitingOverlay) {
                trialWaitingOverlay.style.display = "none";
                trialWaitingOverlay.classList.add("hidden");
            }
            if (trialResultModal) {
                trialResultModal.style.display = "none";
                trialResultModal.classList.add("hidden");
            }
            const localP = localSessionId ? players.get(localSessionId) : undefined;
            if (localP) {
                localP.renderX = localP.targetX;
                localP.renderY = localP.targetY;
                camera.setPosition(localP.targetX, localP.targetY);
                camera.updateZoomForMass(localP.mass);
            }
            try {
                audio.playSuccessSfx?.();
            } catch {
                // ignore
            }
        });

        room.onMessage("trial_hud_update", (data: Record<string, unknown>) => {
            if (data?.arenaWidth && data?.arenaHeight) {
                const aw = Number(data.arenaWidth);
                const ah = Number(data.arenaHeight);
                if (aw > 0 && ah > 0) {
                    currentTrialArenaWidth = aw;
                    currentTrialArenaHeight = ah;
                    if ("setMapDimensions" in activeRenderer && typeof (activeRenderer as any).setMapDimensions === "function") {
                        (activeRenderer as any).setMapDimensions(aw, ah);
                    }
                }
            }
            renderTrialHudState(data);
            if (data?.status === "WAITING_PARTNER") {
                if (trialWaitingOverlay) {
                    trialWaitingOverlay.style.display = "flex";
                    trialWaitingOverlay.classList.remove("hidden");
                }
            } else if (data?.status === "ACTIVE" || !activeTrialPartyCode) {
                if (trialWaitingOverlay) {
                    trialWaitingOverlay.style.display = "none";
                    trialWaitingOverlay.classList.add("hidden");
                }
            }
        });

        room.onMessage("trial_victory", (data: {
            trialId: string;
            title: string;
            icon: string;
            rewardCrystals: number;
            rewardAstralOrbs: number;
            elapsedSeconds: number;
            progress: number;
            targetGoal: number;
        }) => {
            try {
                audio.playSuccessSfx?.();
            } catch {
                // ignore
            }
            if (!trialResultModal) return;
            trialResultModal.style.display = "flex";
            trialResultModal.classList.remove("hidden");
            trialResultModal.innerHTML = `
                <div style="
                    max-width: 460px;
                    width: 100%;
                    padding: 28px 24px;
                    border-radius: 20px;
                    background: linear-gradient(160deg, rgba(15, 23, 42, 0.96) 0%, rgba(9, 13, 22, 0.98) 100%);
                    border: 1px solid rgba(251, 191, 36, 0.65);
                    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.85), 0 0 36px rgba(251, 191, 36, 0.25);
                    text-align: center;
                    color: #f8fafc;
                    font-family: 'Plus Jakarta Sans', sans-serif;
                ">
                    <div style="font-size: 3rem; margin-bottom: 6px;">🏆</div>
                    <h2 style="margin: 0 0 6px; font-size: 1.45rem; font-weight: 900; color: #fde047;">
                        ¡DESAFÍO COMPLETADO!
                    </h2>
                    <p style="margin: 0 0 16px; font-size: 0.92rem; color: #cbd5e1;">
                        Has superado con éxito <b>${data.title || "el reto diario"}</b> en ${data.elapsedSeconds || 0}s.
                    </p>
                    <div style="
                        padding: 14px;
                        border-radius: 14px;
                        background: rgba(30, 41, 59, 0.75);
                        border: 1px solid rgba(56, 189, 248, 0.3);
                        margin-bottom: 18px;
                    ">
                        <div style="font-size: 0.78rem; color: #94a3b8; margin-bottom: 6px; font-weight: 700;">
                            BOTÍN ACREDITADO PARA RECLAMO
                        </div>
                        <div style="display: flex; align-items: center; justify-content: center; gap: 18px; font-size: 1.15rem; font-weight: 900;">
                            <span style="color: #38bdf8;">💎 +${data.rewardCrystals || 0} Cristales</span>
                            <span style="color: #c084fc;">🔮 +${data.rewardAstralOrbs || 0} Orbes</span>
                        </div>
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 10px;">
                        <button type="button" disabled style="
                            width: 100%;
                            padding: 11px 16px;
                            border-radius: 12px;
                            border: 1px dashed rgba(148, 163, 184, 0.4);
                            background: rgba(30, 41, 59, 0.65);
                            color: #94a3b8;
                            font-weight: 800;
                            font-size: 0.85rem;
                            cursor: not-allowed;
                            opacity: 0.72;
                        ">🎬 Duplicar x2 (Próximamente)</button>
                        <button id="btn-trial-victory-claim" type="button" style="
                            width: 100%;
                            padding: 12px 18px;
                            border-radius: 12px;
                            border: 1px solid #6ee7b7;
                            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                            color: #ffffff;
                            font-weight: 800;
                            font-size: 0.95rem;
                            cursor: pointer;
                            box-shadow: 0 0 20px rgba(16, 185, 129, 0.45);
                        ">🎁 Reclamar Recompensa</button>
                    </div>
                </div>
            `;

            const claimBtn = document.getElementById("btn-trial-victory-claim") as HTMLButtonElement | null;
            claimBtn?.addEventListener("click", async () => {
                if (claimBtn) {
                    claimBtn.disabled = true;
                    claimBtn.textContent = "Reclamando...";
                }

                const token = localStorage.getItem("alchem_auth_token") || localStorage.getItem("colyseus-auth-token") || "";
                let resData: any = null;
                try {
                    if (token && token !== "guest_demo_token") {
                        const res = await fetch("/api/trials/claim", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: "Bearer " + token,
                            },
                            body: JSON.stringify({ trialId: data.trialId }),
                        });
                        resData = await res.json().catch(() => ({}));
                        if (res.ok && resData && (typeof resData.newCrystals === "number" || typeof resData.newOrbs === "number")) {
                            const currentProfile = profileManager.getProfile() as { crystals?: number; astralOrbs?: number };
                            const nextCrystals = typeof resData.newCrystals === "number" ? resData.newCrystals : (currentProfile.crystals ?? 0);
                            const nextOrbs = typeof resData.newOrbs === "number" ? resData.newOrbs : (currentProfile.astralOrbs ?? 0);
                            questManager.updateBalances(nextCrystals, nextOrbs);
                            localStorage.setItem("alchem_crystals", String(nextCrystals));
                            localStorage.setItem("alchem_astral_orbs", String(nextOrbs));
                        }
                    }

                    if (trialResultModal) {
                        trialResultModal.style.display = "none";
                        trialResultModal.classList.add("hidden");
                    }

                    const rewardedCrystals = Number(resData?.rewardCrystals ?? data.rewardCrystals ?? 0);
                    const rewardedOrbs = Number(resData?.rewardAstralOrbs ?? data.rewardAstralOrbs ?? 0);
                    await Swal.fire({
                        icon: "success",
                        title: "¡Recompensa Reclamada!",
                        html: `Has ganado <b>+${rewardedCrystals} 💎 Cristales</b> y <b>+${rewardedOrbs} 🔮 Orbes</b> en el desafío <b>${data.title || "Reto Diario"}</b>.`,
                        confirmButtonText: "Aceptar y Volver al Perfil",
                        confirmButtonColor: "#10b981",
                        background: "#0f172a",
                        color: "#f8fafc",
                        allowOutsideClick: false,
                    });
                } catch (claimErr) {
                    console.warn("[DailyTrials] Error al reclamar en arena:", claimErr);
                } finally {
                    document.documentElement.classList.remove("trial-booting");
                    window.location.href = "/perfil.html";
                }
            });
        });

        room.onMessage("trial_defeat", (data: {
            trialId: string;
            title: string;
            reason: string;
            progress: number;
            targetGoal: number;
        }) => {
            try {
                audio.playDeathSfx?.();
            } catch {
                // ignore
            }
            if (!trialResultModal) return;
            trialResultModal.style.display = "flex";
            trialResultModal.classList.remove("hidden");
            trialResultModal.innerHTML = `
                <div style="
                    max-width: 450px;
                    width: 100%;
                    padding: 28px 24px;
                    border-radius: 20px;
                    background: linear-gradient(160deg, rgba(15, 23, 42, 0.96) 0%, rgba(9, 13, 22, 0.98) 100%);
                    border: 1px solid rgba(239, 68, 68, 0.6);
                    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.85), 0 0 32px rgba(239, 68, 68, 0.22);
                    text-align: center;
                    color: #f8fafc;
                    font-family: 'Plus Jakarta Sans', sans-serif;
                ">
                    <div style="font-size: 2.8rem; margin-bottom: 6px;">💀</div>
                    <h2 style="margin: 0 0 6px; font-size: 1.4rem; font-weight: 900; color: #f87171;">
                        DESAFÍO FALLIDO
                    </h2>
                    <p style="margin: 0 0 14px; font-size: 0.95rem; color: #fca5a5; font-weight: 700;">
                        ${data.reason || "¡No se alcanzó la meta a tiempo!"}
                    </p>
                    <div style="
                        padding: 10px 14px;
                        border-radius: 12px;
                        background: rgba(30, 41, 59, 0.7);
                        border: 1px solid rgba(148, 163, 184, 0.25);
                        margin-bottom: 18px;
                        font-size: 0.85rem;
                        color: #cbd5e1;
                    ">
                        Progreso alcanzado: <b>${data.progress ?? 0} / ${data.targetGoal ?? 1}</b>
                    </div>
                    <div style="display: flex; gap: 10px;">
                        <button id="btn-trial-defeat-retry" type="button" style="
                            flex: 1;
                            padding: 12px 16px;
                            border-radius: 12px;
                            border: 1px solid rgba(56, 189, 248, 0.55);
                            background: linear-gradient(135deg, #0284c7 0%, #2563eb 100%);
                            color: #ffffff;
                            font-weight: 800;
                            font-size: 0.9rem;
                            cursor: pointer;
                        ">🔄 Reintentar Reto</button>
                        <button id="btn-trial-defeat-exit" type="button" style="
                            flex: 1;
                            padding: 12px 16px;
                            border-radius: 12px;
                            border: 1px solid rgba(148, 163, 184, 0.4);
                            background: rgba(30, 41, 59, 0.9);
                            color: #e2e8f0;
                            font-weight: 800;
                            font-size: 0.9rem;
                            cursor: pointer;
                        ">Salir al Perfil</button>
                    </div>
                </div>
            `;

            document.getElementById("btn-trial-defeat-retry")?.addEventListener("click", () => {
                if (trialResultModal) {
                    trialResultModal.style.display = "none";
                    trialResultModal.classList.add("hidden");
                }
                room.send("retry-trial");
            });

            document.getElementById("btn-trial-defeat-exit")?.addEventListener("click", () => {
                document.documentElement.classList.remove("trial-booting");
                window.location.href = "/perfil.html";
            });
        });

        (window as unknown as { debugTrial: () => void }).debugTrial = () => {
            console.table({
                isTrialMode,
                activeTrialId,
                activeTrialPartyCode,
                localSessionId,
                playerCount: players.size,
                waitingOverlayDisplay: document.getElementById("trial-waiting-overlay")?.style.display,
                resultModalDisplay: document.getElementById("trial-result-modal")?.style.display,
            });
        };
    }

    function showDeathModal(): void {
        if (!deathOverlay || isSpectating || isTrialMode) return;
        crazyGameplayStop();
        hideCrazyGamesInviteButton();

        const reviveDivider = deathOverlay.querySelector(".revive-divider");

        if (isPrivateRoom) {
            deathSpectateBtn?.classList.add("hidden");
            reviveDivider?.classList.add("hidden");
            rewardedCardsContainer?.classList.add("hidden");
            rewardedCooldownBadge?.classList.add("hidden");

            const now = performance.now();
            const countdownMs = 3000;
            countdownEndsAt = now + countdownMs;
            countdownActive = true;
            countdownPaused = false;

            deathOverlay.classList.remove("hidden");
            if (mobileElementBar) mobileElementBar.classList.add("hidden");
            potionsUI.hideHUD();
            return;
        }

        deathSpectateBtn?.classList.remove("hidden");
        reviveDivider?.classList.remove("hidden");

        // Calcular la duracion del countdown
        const now = performance.now();
        const pokiAvailable = isPokiReady();
        // Cooldown solo activo si ya se uso un rewarded al menos una vez
        const hasUsedRewarded = lastRewardedUsedAt > 0;
        const inCooldown = hasUsedRewarded && (now - lastRewardedUsedAt < REWARDED_COOLDOWN_MS);
        const rewardedAvailable = pokiAvailable && !inCooldown;
        let countdownMs = 3000; // default
        if (pokiAvailable) {
            countdownMs = rewardedAvailable ? 6000 : 4000;
        }
        // Si hay opciones de rewarded, pedir extension al servidor
        if (rewardedAvailable) {
            room.send("request-extended-respawn");
        }
        // Configurar countdown
        countdownEndsAt = now + countdownMs;
        countdownActive = true;
        countdownPaused = false;
        // Rellenar opciones aleatorias de rewarded
        currentRewardedSelection = pickRandomRewards();
        if (opt1Icon) opt1Icon.textContent = currentRewardedSelection[0].icon;
        if (opt1Title) opt1Title.textContent = t(`powerups.${currentRewardedSelection[0].type}Title`, { defaultValue: currentRewardedSelection[0].title });
        if (rewardedOpt1) {
            rewardedOpt1.dataset.rewardType = currentRewardedSelection[0].type;
            const b = rewardedOpt1.querySelector(".reward-bonus");
            if (b) b.textContent = t(`powerups.${currentRewardedSelection[0].type}Bonus`, { defaultValue: currentRewardedSelection[0].bonus });
        }
        if (opt2Icon) opt2Icon.textContent = currentRewardedSelection[1].icon;
        if (opt2Title) opt2Title.textContent = t(`powerups.${currentRewardedSelection[1].type}Title`, { defaultValue: currentRewardedSelection[1].title });
        if (rewardedOpt2) {
            rewardedOpt2.dataset.rewardType = currentRewardedSelection[1].type;
            const b = rewardedOpt2.querySelector(".reward-bonus");
            if (b) b.textContent = t(`powerups.${currentRewardedSelection[1].type}Bonus`, { defaultValue: currentRewardedSelection[1].bonus });
        }
        // Mostrar u ocultar segun disponibilidad (Sprint 31: Ads deshabilitados en modo Próximamente)
        if (rewardedCardsContainer) rewardedCardsContainer.classList.remove("hidden");
        if (rewardedCooldownBadge) rewardedCooldownBadge.classList.add("hidden");

        if (rewardedOpt1) {
            rewardedOpt1.disabled = true;
            rewardedOpt1.style.opacity = "0.6";
            rewardedOpt1.style.cursor = "not-allowed";
            const tag1 = rewardedOpt1.querySelector(".ad-tag");
            if (tag1) tag1.textContent = "🎬 PRÓXIMAMENTE";
        }
        if (rewardedOpt2) {
            rewardedOpt2.disabled = true;
            rewardedOpt2.style.opacity = "0.6";
            rewardedOpt2.style.cursor = "not-allowed";
            const tag2 = rewardedOpt2.querySelector(".ad-tag");
            if (tag2) tag2.textContent = "🎬 PRÓXIMAMENTE";
        }
        // Mostrar overlay
        deathOverlay.classList.remove("hidden");
        if (mobileElementBar) mobileElementBar.classList.add("hidden");
        potionsUI.hideHUD();
    }

    if (reviveInstantBtn) {
        reviveInstantBtn.addEventListener("click", () => {
            // Cancelar countdown
            countdownActive = false;
            countdownPaused = false;
            // Enviar mensaje al servidor
            room.send("request-instant-respawn");
            // Ocultar overlay
            if (deathOverlay) deathOverlay.classList.add("hidden");
            // Track
            trackEvent("revive_instantaneo_click", {});
        });
    }

    async function handleRewardedClick(_rewardType: string): Promise<void> {
        // Sprint 31: Anuncios deshabilitados en modo "Próximamente"
        return;
    }

    if (rewardedOpt1) {
        rewardedOpt1.disabled = true;
        rewardedOpt1.style.opacity = "0.6";
        rewardedOpt1.style.cursor = "not-allowed";
    }
    if (rewardedOpt2) {
        rewardedOpt2.disabled = true;
        rewardedOpt2.style.opacity = "0.6";
        rewardedOpt2.style.cursor = "not-allowed";
    }

    async function maybeTriggerCommercialBreak(): Promise<void> {
        const now = performance.now();
        const sessionDuration = now - sessionStartTime;
        const timeSinceLastBreak = now - lastCommercialBreakAt;

        // Verificar todas las condiciones
        if (consecutiveDeaths < COMMERCIAL_BREAK_DEATHS_THRESHOLD) return;
        if (sessionDuration < COMMERCIAL_BREAK_INITIAL_PROTECTION_MS) return;
        if (lastCommercialBreakAt > 0 && timeSinceLastBreak < COMMERCIAL_BREAK_COOLDOWN_MS) return;
        if (!isPokiReady()) return;

        // Disparar commercial break
        consecutiveDeaths = 0;
        lastCommercialBreakAt = now;

        // Pausar gameplay y audio
        gameplayStop();
        audio.setMuted(true);

        // Track
        trackEvent("commercial_break_mostrado", {
            consecutiveDeaths,
            sessionDuration: Math.round(sessionDuration / 1000),
        });

        // Mostrar el ad
        await showCommercialBreak();

        // Restaurar audio y gameplay
        audio.setMuted(false);
        gameplayStart();
    }

    // 2. Control de input una vez establecida la sala
    inputController = new InputController(canvas, camera, room);

    // ==== Sprint 17: Gestión del Modo Espectador ====
    function enterSpectatorMode(): void {
        isSpectating = true;
        countdownActive = false;
        countdownPaused = false;
        gameplayStop();
        crazyGameplayStop();
        hideCrazyGamesInviteButton();
        if (overlay) overlay.classList.add("hidden");
        if (spawnTooltip) spawnTooltip.classList.add("hidden");
        if (deathOverlay) deathOverlay.classList.add("hidden");
        if (mobileControls) mobileControls.classList.add("hidden");
        if (mobileElementBar) mobileElementBar.classList.add("hidden");
        potionsUI.hideHUD();
        if (spectatorBar) spectatorBar.classList.remove("hidden");
        room.send("enter-spectator");
    }

    function leaveSpectatorMode(): void {
        isSpectating = false;
        spectatedSessionId = null;
        if (spectatorBar) spectatorBar.classList.add("hidden");
        updateMobileControlsVisibility();
        room.send("leave-spectator");
        gameplayStart();
        crazyGameplayStart();
        showCrazyGamesInviteButton(activeInviteCode);
    }

    function cycleSpectatedPlayer(direction: number): void {
        const alivePlayers = Array.from(players.values())
            .filter((p) => p.alive && !p.isSpectator)
            .sort((a, b) => b.mass - a.mass);
        if (alivePlayers.length === 0) {
            spectatedSessionId = null;
            return;
        }
        const currentIndex = alivePlayers.findIndex((p) => p.sessionId === spectatedSessionId);
        let nextIndex = 0;
        if (currentIndex === -1) {
            nextIndex = direction > 0 ? 0 : alivePlayers.length - 1;
        } else {
            nextIndex = (currentIndex + direction + alivePlayers.length) % alivePlayers.length;
        }
        spectatedSessionId = alivePlayers[nextIndex].sessionId;
    }

    if (deathSpectateBtn) {
        deathSpectateBtn.addEventListener("click", () => {
            enterSpectatorMode();
        });
    }

    if (spectatorJoinBtn) {
        spectatorJoinBtn.addEventListener("click", () => {
            leaveSpectatorMode();
        });
    }

    if (spectatorPrevBtn) {
        spectatorPrevBtn.addEventListener("click", () => {
            cycleSpectatedPlayer(-1);
        });
    }

    if (spectatorNextBtn) {
        spectatorNextBtn.addEventListener("click", () => {
            cycleSpectatedPlayer(1);
        });
    }

    const mobileControls = document.getElementById("mobile-controls");
    const joystickZone = document.getElementById("joystick-zone");
    const joystickBase = document.getElementById("joystick-base");
    const joystickStick = document.getElementById("joystick-stick");

    const shouldShowTouchControls = (): boolean => {
        const coarsePointer = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
        const touchPoints = navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 0;
        const mobileUA = /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent);
        const smallViewport = window.innerWidth <= 1024 || window.innerHeight <= 560;
        return isTouchDevice || coarsePointer || touchPoints || mobileUA || smallViewport;
    };

    const updateMobileControlsVisibility = (): void => {
        if (!mobileControls) return;
        if (isSpectating) {
            mobileControls.classList.add("hidden");
            return;
        }
        if (shouldShowTouchControls()) {
            mobileControls.classList.remove("hidden");
        } else {
            mobileControls.classList.add("hidden");
        }
    };
    updateMobileControlsVisibility();
    window.addEventListener("resize", updateMobileControlsVisibility);
    window.addEventListener("orientationchange", updateMobileControlsVisibility);
    window.addEventListener("touchstart", () => {
        if (!isSpectating && mobileControls) mobileControls.classList.remove("hidden");
    }, { passive: true });

    // 1. Lógica multitáctil del Joystick Virtual con PointerEvents
    if (joystickZone && joystickBase && joystickStick) {
        let activePointerId: number | null = null;
        let centerX = 0;
        let centerY = 0;
        const maxStickRadius = 46;

        const updateStickPosition = (clientX: number, clientY: number): void => {
            const dx = clientX - centerX;
            const dy = clientY - centerY;
            const dist = Math.hypot(dx, dy);
            const clampedDist = Math.min(maxStickRadius, dist);
            const angle = Math.atan2(dy, dx);
            const stickX = Math.cos(angle) * clampedDist;
            const stickY = Math.sin(angle) * clampedDist;

            joystickStick.style.transform = `translate(calc(-50% + ${stickX}px), calc(-50% + ${stickY}px))`;
            const normX = dist > 4 ? dx / maxStickRadius : 0;
            const normY = dist > 4 ? dy / maxStickRadius : 0;
            inputController?.setJoystickVector(normX, normY, dist > 4);
        };

        const resetJoystick = (): void => {
            activePointerId = null;
            joystickStick.style.transform = "translate(-50%, -50%)";
            joystickBase.classList.remove("active");
            inputController?.setJoystickVector(0, 0, false);
        };

        joystickZone.addEventListener("pointerdown", (e: PointerEvent) => {
            if (activePointerId !== null) return;
            e.preventDefault();
            activePointerId = e.pointerId;
            joystickZone.setPointerCapture(e.pointerId);
            const baseRect = joystickBase.getBoundingClientRect();
            centerX = baseRect.left + baseRect.width / 2;
            centerY = baseRect.top + baseRect.height / 2;
            joystickBase.classList.add("active");
            updateStickPosition(e.clientX, e.clientY);
            lastMouseMoveTime = performance.now();
        });

        joystickZone.addEventListener("pointermove", (e: PointerEvent) => {
            if (e.pointerId !== activePointerId) return;
            e.preventDefault();
            updateStickPosition(e.clientX, e.clientY);
            lastMouseMoveTime = performance.now();
        });

        joystickZone.addEventListener("pointerup", (e: PointerEvent) => {
            if (e.pointerId === activePointerId) resetJoystick();
        });
        joystickZone.addEventListener("pointercancel", (e: PointerEvent) => {
            if (e.pointerId === activePointerId) resetJoystick();
        });
    }

    // 2. Botón Táctil de Propulsión Alquímica (Turbo)
    if (mobileBoostBtn) {
        const startMobileBoost = (e: Event): void => {
            e.preventDefault();
            mobileBoostBtn.classList.add("active");
            sendBoost(room, true);
            audio.playBoostSfx();
        };
        const stopMobileBoost = (e: Event): void => {
            e.preventDefault();
            mobileBoostBtn.classList.remove("active");
            sendBoost(room, false);
        };
        mobileBoostBtn.addEventListener("pointerdown", startMobileBoost);
        mobileBoostBtn.addEventListener("pointerup", stopMobileBoost);
        mobileBoostBtn.addEventListener("pointercancel", stopMobileBoost);
        mobileBoostBtn.addEventListener("pointerleave", stopMobileBoost);
    }

    // 3. Barra Táctil de Cambio de Elemento (5 elementos)
    if (mobileElementBar) {
        const elButtons = mobileElementBar.querySelectorAll(".mobile-el-btn");
        elButtons.forEach((btn) => {
            btn.addEventListener("pointerdown", (e: Event) => {
                e.preventDefault();
                const el = (btn as HTMLElement).dataset.element;
                if (!el) return;
                if (localSessionId && players.has(localSessionId)) {
                    const lp = players.get(localSessionId)!;
                    if (Date.now() < lp.elementLockedUntil) {
                        return; // Bloqueo elemental activo
                    }
                    if (Date.now() - lp.lastElementChangeAt < ELEMENT_CHANGE_COOLDOWN_MS) {
                        return;
                    }
                }
                room.send("set-element", { element: el });
            });
        });
    }

    // Helper de depuracion: cambiar elemento desde la consola del navegador
    (window as unknown as { setElement: (el: string) => void }).setElement = (el: string) => {
        room.send("set-element", { element: el });
        console.log(`[Debug] Elemento solicitado: ${el}`);
    };
    console.log("[Debug] Tip: usa window.setElement('fire'|'water'|'plant'|'electric'|'earth') para cambiar de elemento.");

    // Atajos de teclado 1-5 para cambiar elemento (solo debug)
    const ELEMENT_KEYS: Record<string, string> = {
        "1": "fire",
        "2": "water",
        "3": "plant",
        "4": "electric",
        "5": "earth",
    };
    // Listener global simplificado: solo maneja atajos especificos
    window.addEventListener("keydown", (e) => {
        // Ignorar si el foco esta en un input de texto visible
        const target = e.target as HTMLElement | null;
        if (target && target.tagName === "INPUT" && (target as HTMLInputElement).type === "text") {
            if (target.offsetParent !== null) return;
        }
        if (target && target.tagName === "TEXTAREA" && target.offsetParent !== null) {
            return;
        }

        if (e.repeat) return;

        if (!e.key || typeof e.key !== "string") return;

        if (e.code === "Space" || e.key === " ") {
            e.preventDefault();
            if (!spacebarHeld) {
                spacebarHeld = true;
                sendBoost(room, true);
                audio.playBoostSfx();
            }
            return;
        }

        const key = e.key.toLowerCase();
        switch (key) {
            case "p":
                accountManager.toggle();
                break;
            case "m":
                toggleAudio();
                break;
            case "q":
                if (room && !isModalOrInputFocused()) {
                    sendUsePotion(room, 0);
                }
                break;
            case "w":
                if (room && !isModalOrInputFocused()) {
                    sendUsePotion(room, 1);
                }
                break;
            case "e":
                if (room && !isModalOrInputFocused()) {
                    sendUsePotion(room, 2);
                }
                break;
            case "?":
            case "/":
                toggleHelpPanel();
                break;
            case "escape":
                closeAllModals();
                break;
            case "k":
                cycleSkin(1);
                break;
            case "j":
                cycleSkin(-1);
                break;
            case "0":
                camera.resetManualZoom();
                console.log("[Zoom] Reset manual");
                break;
            default:
                if (ELEMENT_KEYS[e.key]) {
                    if (localSessionId && players.has(localSessionId)) {
                        const lp = players.get(localSessionId)!;
                        if (Date.now() < lp.elementLockedUntil) {
                            return; // Bloqueo elemental activo
                        }
                        if (Date.now() - lp.lastElementChangeAt < ELEMENT_CHANGE_COOLDOWN_MS) {
                            return;
                        }
                    }
                    room.send("set-element", { element: ELEMENT_KEYS[e.key] });
                }
                break;
        }
    });

    window.addEventListener("keyup", (e) => {
        if (e.code === "Space" || e.key === " ") {
            if (spacebarHeld) {
                spacebarHeld = false;
                sendBoost(room, false);
            }
        }
    });
    window.addEventListener("blur", () => {
        if (spacebarHeld) {
            spacebarHeld = false;
            sendBoost(room, false);
        }
    });

    // Cerrar tooltip al primer movimiento o tecla (una sola vez)
    const dismissTooltip = (): void => {
        if (spawnTooltip) {
            spawnTooltip.classList.add("hidden");
        }
        markTutorialSeen();
        // Marcar paso 1 como completado -> avanzar a movement
        tutorialManager.completeIdentity();
    };

    // El tooltip se cierra al hacer CLICK (no con pointermove, que es demasiado sensible)
    // Se permite cerrar despues de 2s para dar tiempo a leer
    let tooltipCanClose = false;
    setTimeout(() => { tooltipCanClose = true; }, 2000);
    const dismissTooltipNow = (): void => {
        if (!tooltipCanClose) return;
        dismissTooltip();
    };
    window.addEventListener("pointerdown", dismissTooltipNow, { once: true });
    window.addEventListener("keydown", dismissTooltipNow, { once: true });

    function updateCountersForElement(element: string): void {
        const { hunts, fleesFrom } = getElementCounters(element);
        const huntsText = hunts.length > 0 ? hunts.map(h => t(`elements.${h}`, { defaultValue: h })).join(", ").toUpperCase() : t("hud.none");
        const fleesText = fleesFrom.length > 0 ? fleesFrom.map(f => t(`elements.${f}`, { defaultValue: f })).join(", ").toUpperCase() : t("hud.none");

        // HUD counters (permanentes)
        if (huntTarget) {
            huntTarget.textContent = huntsText;
            huntTarget.style.color = ELEMENT_COLORS[hunts[0]] || "#38bdf8";
        }
        if (fleeTarget) {
            fleeTarget.textContent = fleesText;
            fleeTarget.style.color = ELEMENT_COLORS[fleesFrom[0]] || "#38bdf8";
        }
        // Tooltip (solo si está visible)
        if (spawnElement) spawnElement.textContent = t(`elements.${element}`, { defaultValue: element }).toUpperCase();
        if (spawnHunt) {
            spawnHunt.textContent = huntsText;
            spawnHunt.style.color = ELEMENT_COLORS[hunts[0]] || "#38bdf8";
        }
        if (spawnFlee) {
            spawnFlee.textContent = fleesText;
            spawnFlee.style.color = ELEMENT_COLORS[fleesFrom[0]] || "#38bdf8";
        }
    }



    // 3. Suscripción a eventos y sincronización de estado de jugadores
    onRoomStateChange(room, {
        onPlayerAdd: (sessionId: string, state: IPlayerNetworkState) => {
            localSessionId = room.sessionId || localSessionId;
            const player = createInterpolatedPlayer(
                sessionId,
                state.x,
                state.y,
                state.mass,
                state.element,
                state.alias
            );
            player.renderX = state.x;
            player.renderY = state.y;
            player.targetX = state.x;
            player.targetY = state.y;
            player.mass = state.mass;
            player.alive = state.alive;
            player.initialized = true;
            player.respawnAt = state.respawnAt;
            player.isBot = state.isBot;
            player.kills = state.kills;
            player.deaths = state.deaths;
            player.invulnerableUntil = state.invulnerableUntil;
            player.speedBoostUntil = state.speedBoostUntil;
            player.hasShield = state.hasShield;
            player.doubleMassUntil = state.doubleMassUntil;
            player.frozenUntil = state.frozenUntil;
            player.freezeCooldownUntil = state.freezeCooldownUntil;
            player.skin = state.skin;
            player.cosmetics = sessionId === localSessionId ? currentCosmetics : parsePlayerCosmetics(state.skin);
            player.lastElementChangeAt = state.lastElementChangeAt;
            player.killstreak = state.killstreak;
            player.isBoosting = state.isBoosting;
            player.isSpectator = state.isSpectator;
            player.drinkingUntil = state.drinkingUntil;
            player.magnetUntil = state.magnetUntil;
            player.elementLockedUntil = state.elementLockedUntil;
            player.potionCooldownUntil = state.potionCooldownUntil;
            player.activePotionEffect = state.activePotionEffect;
            player.potionSlot0 = state.potionSlot0;
            player.potionSlot1 = state.potionSlot1;
            player.potionSlot2 = state.potionSlot2;
            player.cosmicFoodUntil = state.cosmicFoodUntil || 0;
            player.rebirthUntil = state.rebirthUntil || 0;
            player.equippedTitle = state.equippedTitle || "";
            player.petBuffActive = Boolean(state.petBuffActive);
            player.petBuffType = state.petBuffType || "";
            player.petBuffUntil = Number(state.petBuffUntil || 0);
            player.petNextBuffAt = Number(state.petNextBuffAt || 0);
            players.set(sessionId, player);
            if (isPrivateRoom) {
                updatePrivateHudCount();
            }

            // Onboarding: si es el jugador local y no vio el tutorial, registrar evento y arrancar gameplay (solo si no es espectador)
            if (sessionId === localSessionId) {
                camera.setPosition(state.x, state.y);
                camera.updateZoomForMass(state.mass);
                if (isTrialMode) {
                    const trialWaitingOverlay = document.getElementById("trial-waiting-overlay");
                    const trialResultModal = document.getElementById("trial-result-modal");
                    if (trialWaitingOverlay && (!activeTrialPartyCode || activeTrialPartyCode.startsWith("BOT_ALLY_")) && (activeTrialId !== "trial_coop_purge" || botAllyParam)) {
                        trialWaitingOverlay.style.display = "none";
                        trialWaitingOverlay.classList.add("hidden");
                    }
                    if (trialResultModal) {
                        trialResultModal.style.display = "none";
                        trialResultModal.classList.add("hidden");
                    }
                    console.log(`[Trial Debug] Jugador local posicionado en (${state.x}, ${state.y}), cámara fijada, overlays forzados a display:none.`);
                }
                updateCountersForElement(state.element);
                trackEvent("partida_iniciada", { alias: state.alias, element: state.element });
                if (!startAsSpectator && !isSpectating && !state.isSpectator) {
                    gameplayStart();
                    crazyGameplayStart();
                    showCrazyGamesInviteButton(activeInviteCode);
                }
            }
        },
        onPlayerRemove: (sessionId: string) => {
            players.delete(sessionId);
            if (isPrivateRoom) {
                updatePrivateHudCount();
            }
        },
        onPlayerChange: (sessionId: string, field: keyof IPlayerNetworkState, value: unknown) => {
            const player = players.get(sessionId);
            if (!player) return;

            if (field === "x") {
                player.targetX = value as number;
                player.lastServerUpdate = performance.now();
            } else if (field === "y") {
                player.targetY = value as number;
                player.lastServerUpdate = performance.now();
            } else if (field === "mass") {
                const newMass = value as number;
                const massDelta = newMass - player.mass;
                player.mass = newMass;
                // Detección heurística: un salto de masa >= 3 comidas en un tick sugiere absorción de jugador.
                // Falso positivo posible si se comen 3+ comidas simultáneamente. Sprint 8 lo resolverá con un mensaje del servidor.
                if (massDelta >= FOOD_MASS * 3) {
                    absorbPulses.set(sessionId, performance.now());
                }
                if (sessionId === localSessionId) {
                    if (!isPrivateRoom && !isTrialMode) {
                        profileManager.recordMass(newMass);
                        questManager.trackMass(newMass);
                    }
                    // Tutorial paso 3: contar comidas comidas
                    if (tutorialManager.getCurrentStep() === "growth" && massDelta > 0 && massDelta <= FOOD_MASS * 1.5) {
                        // Es una comida (no una absorcion grande)
                        if (massDelta >= FOOD_MASS * 0.75) {
                            tutorialManager.addFoodEaten();
                        }
                    }
                }

            } else if (field === "element") {
                player.element = value as string;
                if (sessionId === localSessionId) {
                    updateCountersForElement(player.element);
                }
            } else if (field === "alive") {
                const wasAlive = player.alive;
                const nowAlive = value as boolean;
                player.alive = nowAlive;
                // Transición true -> false: crear animación de muerte
                if (wasAlive && !nowAlive) {
                    const radius = Math.sqrt(player.mass) * 2;
                    deaths.push({
                        sessionId: player.sessionId,
                        x: player.renderX,
                        y: player.renderY,
                        startRadius: radius,
                        element: player.element,
                        startTime: performance.now(),
                    });
                    if (sessionId === localSessionId && !player.isSpectator) {
                        audio.playDeathSfx();
                        const duration = performance.now() - player.lifeStartTime;
                        if (!isPrivateRoom && !isTrialMode) {
                            questManager.trackSurvivalTime(duration);
                            void questManager.flush();
                        }

                        trackEvent("partida_terminada", {
                            duration: Math.round(duration / 1000),
                            finalMass: player.mass,
                            kills: player.kills,
                            deaths: player.deaths,
                        });
                        gameplayStop();
                        crazyGameplayStop();
                        setTimeout(() => {
                            if (!player.alive) {
                                showDeathModal();
                            }
                        }, 850);
                        // Si es el jugador local, incrementar muertes consecutivas
                        consecutiveDeaths++;
                        // Verificar si se debe disparar un commercial break
                        maybeTriggerCommercialBreak();
                    }
                }
                // Transición false -> true: crear anillo de respawn
                if (!wasAlive && nowAlive) {
                    respawnRings.push({
                        x: player.renderX,
                        y: player.renderY,
                        color: ELEMENT_COLORS[player.element] || "#ffffff",
                        startTime: performance.now(),
                        duration: 500,
                    });
                    if (sessionId === localSessionId) {
                        player.renderX = player.targetX;
                        player.renderY = player.targetY;
                        camera.setPosition(player.targetX, player.targetY);
                        camera.updateZoomForMass(player.mass);
                        player.lifeStartTime = performance.now();
                        // Tras respawn exitoso del jugador local
                        gameplayStart();
                        crazyGameplayStart();
                        showCrazyGamesInviteButton(activeInviteCode);
                    }
                }
            } else if (field === "respawnAt") {
                player.respawnAt = value as number;
            } else if (field === "isBot") {
                player.isBot = value as boolean;
            } else if (field === "kills") {
                const newKills = value as number;
                const delta = newKills - player.kills;
                player.kills = newKills;
                if (sessionId === localSessionId && delta > 0) {
                    if (!isPrivateRoom && !isTrialMode) {
                        profileManager.recordKill(delta);
                        questManager.trackKill(player.element, false);
                    }
                    // Resetear muertes consecutivas al hacer un kill
                    consecutiveDeaths = 0;
                    camera.triggerShake(10, 240);
                    if (!isPrivateRoom && !isTrialMode) {
                        void profileManager.syncToDatabase();
                    }
                }
            } else if (field === "deaths") {
                const newDeaths = value as number;
                const delta = newDeaths - player.deaths;
                player.deaths = newDeaths;
                if (sessionId === localSessionId && delta > 0) {
                    if (!isPrivateRoom && !isTrialMode) {
                        profileManager.recordDeath();
                        void profileManager.syncToDatabase();
                    }
                }
            } else if (field === "alias") {
                player.alias = value as string;
            } else if (field === "invulnerableUntil") {
                player.invulnerableUntil = value as number;
            } else if (field === "speedBoostUntil") {
                player.speedBoostUntil = value as number;
            } else if (field === "hasShield") {
                player.hasShield = value as boolean;
            } else if (field === "doubleMassUntil") {
                player.doubleMassUntil = value as number;
            } else if (field === "frozenUntil") {
                player.frozenUntil = value as number;
            } else if (field === "freezeCooldownUntil") {
                player.freezeCooldownUntil = value as number;
            } else if (field === "skin") {
                player.skin = value as string;
                if (player.sessionId !== localSessionId) {
                    player.cosmetics = parsePlayerCosmetics(value as string);
                }
            } else if (field === "lastElementChangeAt") {
                player.lastElementChangeAt = value as number;
            } else if (field === "killstreak") {
                player.killstreak = value as number;
            } else if (field === "isBoosting") {
                player.isBoosting = value as boolean;
            } else if (field === "isSpectator") {
                player.isSpectator = value as boolean;
            } else if (field === "drinkingUntil") {
                player.drinkingUntil = value as number;
            } else if (field === "magnetUntil") {
                player.magnetUntil = value as number;
            } else if (field === "elementLockedUntil") {
                player.elementLockedUntil = value as number;
            } else if (field === "potionCooldownUntil") {
                player.potionCooldownUntil = value as number;
            } else if (field === "activePotionEffect") {
                player.activePotionEffect = value as string;
            } else if (field === "potionSlot0") {
                player.potionSlot0 = value as string;
            } else if (field === "potionSlot1") {
                player.potionSlot1 = value as string;
            } else if (field === "potionSlot2") {
                player.potionSlot2 = value as string;
            } else if (field === "cosmicFoodUntil") {
                player.cosmicFoodUntil = value as number;
            } else if (field === "rebirthUntil") {
                player.rebirthUntil = value as number;
            } else if (field === "equippedTitle") {
                player.equippedTitle = (value as string) || "";
            } else if ((field as string) === "petBuffActive") {
                player.petBuffActive = Boolean(value);
            } else if ((field as string) === "petBuffType") {
                player.petBuffType = (value as string) || "";
            } else if ((field as string) === "petBuffUntil") {
                player.petBuffUntil = Number(value || 0);
            } else if ((field as string) === "petNextBuffAt") {
                player.petNextBuffAt = Number(value || 0);
            }
        },
        onFoodAdd: (foodId: string, food: IFoodNetworkState) => {
            foods.set(foodId, { x: food.x, y: food.y });
        },
        onFoodRemove: (foodId: string) => {
            const food = foods.get(foodId);
            if (food) {
                const now = performance.now();
                const shape = foodShapeFromId(foodId);
                const particleColor = FOOD_COLORS[shape] || "#e2e8f0";

                // Buscar el jugador vivo más cercano que pudo absorber esta comida
                let absorberId: string | undefined = undefined;
                let minDistSq = Infinity;
                for (const p of players.values()) {
                    if (!p.alive) continue;
                    const pRadius = Math.sqrt(p.mass) * 2 + 45;
                    const dx = p.renderX - food.x;
                    const dy = p.renderY - food.y;
                    const dSq = dx * dx + dy * dy;
                    if (dSq <= pRadius * pRadius && dSq < minDistSq) {
                        minDistSq = dSq;
                        absorberId = p.sessionId;
                    }
                }

                if (absorberId === localSessionId) {
                    audio.playEatSfx();
                    if (!isPrivateRoom && !isTrialMode) {
                        questManager.trackFoodEaten(1);
                    }
                }

                for (let i = 0; i < 4; i++) {
                    const angle = (i / 4) * Math.PI * 2 + Math.random() * 0.8;
                    const speed = 0.6 + Math.random() * 0.6;
                    foodParticles.push({
                        x: food.x,
                        y: food.y,
                        vx: Math.cos(angle) * speed,
                        vy: Math.sin(angle) * speed,
                        color: particleColor,
                        startTime: now,
                        duration: 360,
                        targetSessionId: absorberId,
                    });
                }
            }
            foods.delete(foodId);
        },
        onFoodChange: (foodId: string, field: keyof IFoodNetworkState, value: unknown) => {
            const existing = foods.get(foodId);
            if (!existing) return;
            if (field === "x") existing.x = value as number;
            else if (field === "y") existing.y = value as number;
        },
        onElementalHit: (payload) => {
            elementalHits.push({
                id: `hit_${nextElementalHitId++}`,
                x: payload.x,
                y: payload.y,
                effectType: payload.effectType,
                damage: payload.damage,
                startTime: performance.now(),
                duration: ELEMENTAL_HIT_DURATION_MS,
            });
            const screen = camera.worldToScreen(payload.x, payload.y);
            const { width, height } = camera.getViewport();
            const inViewport = screen.x >= -50 && screen.x <= width + 50 && screen.y >= -50 && screen.y <= height + 50;
            const involvesLocal = payload.attackerId === localSessionId || payload.defenderId === localSessionId;
            if (involvesLocal || inViewport) {
                audio.playCounterSfx();
            }
            if (involvesLocal) {
                camera.triggerShake(7, 180);
            }
            if (payload.attackerId === localSessionId) {
                if (!isPrivateRoom && !isTrialMode) {
                    questManager.trackCounterDamage(payload.damage);
                }
            }
            trackEvent("counter_aplicado", { effectType: payload.effectType, damage: payload.damage });
        },
        onPowerUpAdd: (powerUpId: string, pw: { x: number; y: number; type: string }) => {
            powerUps.set(powerUpId, {
                x: pw.x,
                y: pw.y,
                type: pw.type as PowerUpType,
            });
        },
        onPowerUpRemove: (powerUpId: string) => {
            powerUps.delete(powerUpId);
        },
        onPowerUpCollected: (payload) => {
            if (payload.playerId === localSessionId) {
                audio.playPowerUpSfx();
                if (!isPrivateRoom && !isTrialMode) {
                    questManager.trackPowerUpCollected();
                }
            }
            // Crear efecto visual de pickup
            powerUpPickups.push({
                id: `pickup_${nextPickupEffectId++}`,
                x: payload.x,
                y: payload.y,
                type: payload.type as PowerUpType,
                startTime: performance.now(),
                duration: 500,
            });
            trackEvent("powerup_recogido", { type: payload.type });
        },
        onShieldBreak: (payload) => {
            // Crear efecto visual: anillo azul expansivo
            elementalHits.push({
                id: `shield_${nextElementalHitId++}`,
                x: payload.x,
                y: payload.y,
                effectType: "shield-break",
                damage: 0,
                startTime: performance.now(),
                duration: 500,
            });
        },
        onFreezeBurst: (payload) => {
            // Anillo expansivo azul hielo en el radio del freeze
            elementalHits.push({
                id: `freeze_${nextElementalHitId++}`,
                x: payload.x,
                y: payload.y,
                effectType: "freeze-burst",
                damage: payload.radius, // Reutilizamos damage para el radio
                startTime: performance.now(),
                duration: 800,
            });
            if (payload.frozenPlayerIds.length === 0) {
                console.log("[Freeze] Nadie fue congelado (nadie cerca).");
            } else {
                console.log(`[Freeze] ${payload.frozenPlayerIds.length} jugador(es) congelado(s).`);
            }
        },
        onEventWarning: (payload) => {
            console.log("[Event] Warning recibido en cliente:", payload);
            if (!eventBanner || !eventBannerIcon || !eventBannerSubtitle) return;

            if (eventWarningInterval) {
                clearInterval(eventWarningInterval);
                eventWarningInterval = null;
            }

            const eventKey = payload.type === "food-rain" ? "events.foodRain" :
                             payload.type === "meteor-shower" ? "events.meteorShower" :
                             payload.type === "poison-zone" ? "events.poisonZone" :
                             payload.type === "gravity-well" ? "events.gravityWell" : null;
            const eventName = (payload.name && payload.name.trim().length > 0) ? payload.name : (eventKey ? t(eventKey) : payload.name);
            eventBannerIcon.textContent = payload.emoji;
            if (eventBannerTitle) eventBannerTitle.textContent = payload.isPositive ? t("events.nextEvent") : t("events.nextDanger");

            let remainingSeconds = Math.max(1, Math.ceil((payload.startsInMs || 5000) / 1000));
            eventBannerSubtitle.textContent = `⚠️ ¡${eventName.toUpperCase()} EN ${remainingSeconds}s!`;
            eventBanner.classList.remove("hidden");
            eventBanner.classList.add("event-warning-urgent");

            eventWarningInterval = setInterval(() => {
                remainingSeconds--;
                if (remainingSeconds > 0) {
                    if (eventBannerSubtitle) {
                        eventBannerSubtitle.textContent = `⚠️ ¡${eventName.toUpperCase()} EN ${remainingSeconds}s!`;
                    }
                } else {
                    if (eventWarningInterval) {
                        clearInterval(eventWarningInterval);
                        eventWarningInterval = null;
                    }
                    eventBanner?.classList.remove("event-warning-urgent");
                }
            }, 1000);
        },
        onEventStart: (payload) => {
            if (eventWarningInterval) {
                clearInterval(eventWarningInterval);
                eventWarningInterval = null;
            }
            if (eventBanner) {
                eventBanner.classList.remove("event-warning-urgent");
                eventBanner.classList.add("hidden");
            }

            const eventKey = payload.type === "food-rain" ? "events.foodRain" :
                             payload.type === "meteor-shower" ? "events.meteorShower" :
                             payload.type === "poison-zone" ? "events.poisonZone" :
                             payload.type === "gravity-well" ? "events.gravityWell" : null;
            const eventName = (payload.name && payload.name.trim().length > 0) ? payload.name : (eventKey ? t(eventKey) : payload.name);
            activeEvent = {
                type: payload.type,
                name: eventName,
                emoji: payload.emoji,
                color: payload.color,
                isPositive: payload.isPositive,
                x: payload.x,
                y: payload.y,
                radius: payload.radius,
                startAt: performance.now(),
                endAt: performance.now() + payload.durationMs,
            };
            // Track
            trackEvent("event_start", { type: payload.type, isPositive: payload.isPositive });
        },
        onEventEnd: () => {
            activeEvent = null;
            trackEvent("event_end", {});
        },
        onComboHit: (payload) => {
            floatingTexts.push({
                id: `ft_${nextFloatingTextId++}`,
                x: payload.x,
                y: payload.y,
                text: t("combat.comboText", { count: payload.combo }),
                subtext: t("combat.massBonus", { count: payload.bonusMass }),
                color: "#facc15",
                startTime: performance.now(),
                duration: 1100,
            });
            const screen = camera.worldToScreen(payload.x, payload.y);
            const { width, height } = camera.getViewport();
            const inViewport = screen.x >= -50 && screen.x <= width + 50 && screen.y >= -50 && screen.y <= height + 50;
            if (payload.playerId === localSessionId || inViewport) {
                audio.playComboSfx(payload.combo);
            }
            if (payload.playerId === localSessionId) {
                camera.triggerShake(6 + Math.min(8, payload.combo * 2), 200);
            }
        },
        onKillstreakTier: (payload) => {
            const streakKey = payload.streak === 3 ? "combat.dominating" :
                              payload.streak === 5 ? "combat.unstoppable" :
                              payload.streak === 8 ? "combat.bloodthirsty" :
                              payload.streak === 12 ? "combat.alchemyGod" : null;
            const streakTitle = streakKey ? t(streakKey) : payload.title;
            floatingTexts.push({
                id: `ft_${nextFloatingTextId++}`,
                x: payload.x,
                y: payload.y,
                text: `${payload.emoji} ${streakTitle} (${payload.streak})`,
                subtext: payload.alias,
                color: payload.color,
                startTime: performance.now(),
                duration: 1500,
            });
        },
        onKillstreakShutdown: (payload) => {
            if (payload.killerId === localSessionId) {
                if (!isPrivateRoom && !isTrialMode) {
                    questManager.addProgress("shutdown_bounty", 1);
                }
            }
            floatingTexts.push({
                id: `ft_${nextFloatingTextId++}`,
                x: payload.x,
                y: payload.y,
                text: t("combat.shutdownTitle"),
                subtext: t("combat.shutdownDesc", { killer: payload.killerAlias, victim: payload.victimAlias, bounty: payload.bountyMass }),
                color: "#38bdf8",
                startTime: performance.now(),
                duration: 1600,
            });
        },
        onTitleUnlocked: (payload) => {
            floatingTexts.push({
                id: `ft_${nextFloatingTextId++}`,
                x: payload.x,
                y: payload.y,
                text: "👑 ¡TÍTULO DESBLOQUEADO!",
                subtext: `${payload.icon} ${payload.titleName} (${payload.alias})`,
                color: payload.primaryColor || "#fbbf24",
                startTime: performance.now(),
                duration: 2200,
            });
            try {
                audio.playSuccessSfx?.();
            } catch {
                // ignore
            }
        },
        onEssenceBurst: (data) => {
            deaths.push({
                sessionId: "essence_" + performance.now(),
                x: data.x,
                y: data.y,
                startRadius: data.radius || 28,
                element: data.element,
                startTime: performance.now(),
            });
            if (data.reason === "evasion") {
                floatingTexts.push({
                    id: `ft_evasion_${nextFloatingTextId++}`,
                    x: data.x,
                    y: data.y,
                    text: `💨 ¡HUIDA! +${data.massDropped} ESENCIA`,
                    subtext: data.alias ? `${data.alias} abandonó` : "",
                    color: "#38bdf8",
                    startTime: performance.now(),
                    duration: 2500,
                });
            }
        },
    });

    room.onMessage("meteor-spawn", (_data: { x: number; y: number; impactAt: number }) => {
        // El render lee el estado sincronizado (state.meteors)
    });

    room.onMessage("gravity-capture", (data: { sessionId: string; x: number; y: number }) => {
        gravityCaptures.push({
            x: data.x,
            y: data.y,
            startTime: performance.now(),
            duration: 600,
        });
    });

    // Sprint 30: Activación de Emote Social en la arena
    room.onMessage("player-emote", (data: { sessionId: string; emoteId: string }) => {
        if (data && data.sessionId && data.emoteId) {
            activeEmotes.set(data.sessionId, {
                emoteId: data.emoteId,
                startTime: performance.now(),
                duration: EMOTE_DURATION_MS,
            });
        }
    });

    // Sistema de Poderes y Afinidad de Mascotas: Audio de activación
    room.onMessage("pet-buff-activated", (data: { playerId: string; element: string; buffType: string }) => {
        if (data && data.playerId === localSessionId) {
            audio.playPetActivationSound();
        }
    });

    // 4. Ocultar el overlay de bienvenida al conectarse exitosamente
    if (overlay) {
        overlay.classList.add("hidden");
    }

    if (startAsSpectator) {
        enterSpectatorMode();
    }

    // 5. HUD de buffs activos
    function updateBuffHud(): void {
        if (!buffHud) return;
        if (!localSessionId || !players.has(localSessionId)) {
            buffHud.classList.add("hidden");
            return;
        }
        const localPlayer = players.get(localSessionId)!;
        const now = Date.now();
        const activeBuffs: Array<{ key: string; config: (typeof POWERUP_CONFIG)[PowerUpType]; remaining: number | null }> = [];

        if (localPlayer.invulnerableUntil > now) {
            activeBuffs.push({ key: "invulnerability", config: POWERUP_CONFIG.invulnerability, remaining: localPlayer.invulnerableUntil - now });
        }
        if (localPlayer.speedBoostUntil > now && localPlayer.activePotionEffect !== "speed") {
            activeBuffs.push({ key: "speed", config: POWERUP_CONFIG.speed, remaining: localPlayer.speedBoostUntil - now });
        }
        if (localPlayer.hasShield) {
            activeBuffs.push({ key: "shield", config: POWERUP_CONFIG.shield, remaining: null });
        }
        if (localPlayer.doubleMassUntil > now) {
            activeBuffs.push({ key: "double-mass", config: POWERUP_CONFIG["double-mass"], remaining: localPlayer.doubleMassUntil - now });
        }
        if (localPlayer.frozenUntil > now) {
            activeBuffs.push({ key: "freeze", config: POWERUP_CONFIG.freeze, remaining: localPlayer.frozenUntil - now });
        }

        const badgesHtml = activeBuffs.map((buff) => {
            const remainingText = buff.remaining !== null ? `${(buff.remaining / 1000).toFixed(1)}s` : "1x";
            const expiring = buff.remaining !== null && buff.remaining < 2000;
            return `<div class="buff-badge ${expiring ? "expiring" : ""}" style="color: ${buff.config.color}; border-color: ${buff.config.color}40;">
                <span class="buff-badge-icon">${buff.config.emoji}</span>
                <span class="buff-badge-timer">${remainingText}</span>
            </div>`;
        });

        // Sprint 36: Indicadores de estado de Mascota en el HUD (Buff activo y Cooldown de recarga)
        const localSkin = localPlayer.skin;
        let localCosmetics: PlayerCosmeticsConfig | null = currentCosmetics;
        if (localSkin && localSkin.startsWith("{")) {
            try {
                localCosmetics = parsePlayerCosmetics(localSkin);
            } catch {
                // ignore
            }
        }
        const hasFamiliar = Boolean(localCosmetics?.familiar && localCosmetics.familiar !== "none");

        if (hasFamiliar) {
            if (localPlayer.petBuffActive && localPlayer.petBuffUntil && localPlayer.petBuffUntil > now) {
                const rem = ((localPlayer.petBuffUntil - now) / 1000).toFixed(1);
                badgesHtml.push(`<div class="buff-badge expiring pet-buff-active-badge" style="color: #fbbf24; border-color: rgba(251, 191, 36, 0.7); box-shadow: 0 0 14px rgba(251, 191, 36, 0.5);">
                    <span class="buff-badge-icon">🐾⚡</span>
                    <span class="buff-badge-timer">${rem}s</span>
                </div>`);
            } else if (!localPlayer.petBuffActive && localPlayer.petNextBuffAt && localPlayer.petNextBuffAt > now) {
                const rec = Math.ceil((localPlayer.petNextBuffAt - now) / 1000);
                badgesHtml.push(`<div class="buff-badge pet-buff-cooldown-badge" style="color: #94a3b8; border-color: rgba(148, 163, 184, 0.35);">
                    <span class="buff-badge-icon">🐾💤</span>
                    <span class="buff-badge-timer">${rec}s</span>
                </div>`);
            }
        }

        if (badgesHtml.length === 0) {
            buffHud.classList.add("hidden");
            buffHud.innerHTML = "";
            return;
        }

        buffHud.classList.remove("hidden");
        buffHud.innerHTML = badgesHtml.join("");
    }

    // 6. Bucle de renderizado e interpolación a 60 FPS
    function gameLoop(timestamp: number): void {
        requestAnimationFrame(gameLoop);
        try {
            const deltaSeconds = Math.min(0.1, Math.max(0.001, lastFrameTime ? (timestamp - lastFrameTime) / 1000 : 0.016));
            lastFrameTime = timestamp;
            const nowMs = performance.now();

            if (attractModeActive) {
                // ==== ATTRACT MODE ====
                // Mover bots de forma envolvente cruzando la pantalla
                for (const bot of attractBots) {
                    const dx = bot.targetX - bot.x;
                    const dy = bot.targetY - bot.y;
                    const dist = Math.hypot(dx, dy);
                    if (dist < 20) {
                        // Asignar nuevo objetivo en el lado opuesto para que cruce la pantalla visiblemente
                        const currentAngle = Math.atan2(bot.y - 1500, bot.x - 1500);
                        const oppositeAngle = currentAngle + Math.PI + (Math.random() - 0.5) * 1.2;
                        const targetDist = 350 + Math.random() * 850;
                        bot.targetX = 1500 + Math.cos(oppositeAngle) * targetDist;
                        bot.targetY = 1500 + Math.sin(oppositeAngle) * targetDist;
                    } else {
                        const speed = 190 * deltaSeconds;
                        bot.x += (dx / dist) * speed;
                        bot.y += (dy / dist) * speed;
                    }
                }

                // Cámara orbitando alrededor del centro con radio de 450px
                attractCameraAngle += deltaSeconds * 0.08;
                const camX = 1500 + Math.cos(attractCameraAngle) * 450;
                const camY = 1500 + Math.sin(attractCameraAngle) * 450;
                camera.setPosition(camX, camY);
                camera.updateZoomForMass(180);

                // Construir renderizables falsos con orientación y estiramiento usando pools
                let botIdx = 0;
                for (const bot of attractBots) {
                    const dx = bot.targetX - bot.x;
                    const dy = bot.targetY - bot.y;
                    const fAngle = Math.atan2(dy, dx);
                    const speed = 190;

                    let rp = pooledRenderablePlayers[botIdx];
                    if (!rp) {
                        rp = {
                            sessionId: bot.id,
                            renderX: bot.x,
                            renderY: bot.y,
                            mass: bot.mass,
                            element: bot.element,
                            isLocal: false,
                            alive: true,
                            isBot: true,
                            alias: `Alchemist_${bot.id.slice(-2)}`,
                            invulnerableUntil: 0,
                            speedBoostUntil: 0,
                            hasShield: false,
                            doubleMassUntil: 0,
                            frozenUntil: 0,
                            skin: "default",
                            cosmetics: undefined,
                            vx: Math.cos(fAngle) * speed,
                            vy: Math.sin(fAngle) * speed,
                            facingAngle: fAngle,
                            killstreak: 0,
                            isBoosting: false,
                            kills: 0,
                            drinkingUntil: 0,
                            magnetUntil: 0,
                            elementLockedUntil: 0,
                            potionCooldownUntil: 0,
                            activePotionEffect: "",
                            potionSlot0: "",
                            potionSlot1: "",
                            potionSlot2: "",
                            cosmicFoodUntil: 0,
                            rebirthUntil: 0,
                        };
                        pooledRenderablePlayers[botIdx] = rp;
                    } else {
                        rp.sessionId = bot.id;
                        rp.renderX = bot.x;
                        rp.renderY = bot.y;
                        rp.mass = bot.mass;
                        rp.element = bot.element;
                        rp.isLocal = false;
                        rp.alive = true;
                        rp.isBot = true;
                        rp.alias = `Alchemist_${bot.id.slice(-2)}`;
                        rp.invulnerableUntil = 0;
                        rp.speedBoostUntil = 0;
                        rp.hasShield = false;
                        rp.doubleMassUntil = 0;
                        rp.frozenUntil = 0;
                        rp.skin = "default";
                        rp.cosmetics = undefined;
                        rp.vx = Math.cos(fAngle) * speed;
                        rp.vy = Math.sin(fAngle) * speed;
                        rp.facingAngle = fAngle;
                        rp.killstreak = 0;
                        rp.isBoosting = false;
                        rp.kills = 0;
                        rp.drinkingUntil = 0;
                        rp.magnetUntil = 0;
                        rp.elementLockedUntil = 0;
                        rp.potionCooldownUntil = 0;
                        rp.activePotionEffect = "";
                        rp.potionSlot0 = "";
                        rp.potionSlot1 = "";
                        rp.potionSlot2 = "";
                        rp.cosmicFoodUntil = 0;
                        rp.rebirthUntil = 0;
                    }
                    botIdx++;
                }
                pooledRenderablePlayers.length = botIdx;

                let afIdx = 0;
                for (const f of attractFoods) {
                    let rf = pooledRenderableFoods[afIdx];
                    if (!rf) {
                        rf = { x: f.x, y: f.y, shape: f.shape };
                        pooledRenderableFoods[afIdx] = rf;
                    } else {
                        rf.x = f.x;
                        rf.y = f.y;
                        rf.shape = f.shape;
                    }
                    afIdx++;
                }
                pooledRenderableFoods.length = afIdx;

                activeRenderer.render(
                    pooledRenderablePlayers,
                    pooledRenderableFoods,
                    [],
                    [],
                    null,
                    new Map(),
                    new Map(),
                    nowMs,
                    [],
                    [],
                    currentCosmetics,
                    [],
                    [],
                    null,
                    [],
                    [],
                    [],
                    [],
                    attractModeActive
                );
                return; // Saltar el resto de la lógica de red
            }

            interpolatePlayers(deltaSeconds);

            // La cámara sigue al objetivo observado en modo espectador o al jugador local
            if (isSpectating) {
                const alivePlayers = Array.from(players.values())
                    .filter((p) => p.alive && !p.isSpectator)
                    .sort((a, b) => b.mass - a.mass);

                let target: InterpolatedPlayer | undefined;
                if (spectatedSessionId) {
                    target = alivePlayers.find((p) => p.sessionId === spectatedSessionId);
                }
                if (!target && alivePlayers.length > 0) {
                    target = alivePlayers[0];
                    spectatedSessionId = target.sessionId;
                }

                if (target) {
                    camera.follow(target.renderX, target.renderY, 0.12);
                    camera.updateZoomForMass(target.mass);
                    if (spectatorTargetName) {
                        spectatorTargetName.textContent = `👁️ ${target.alias} (${Math.round(target.mass)} ${t("hud.mass").toLowerCase()})`;
                    }
                } else {
                    if (spectatorTargetName) {
                        spectatorTargetName.textContent = t("hud.observingArena");
                    }
                }
            } else if (localSessionId && players.has(localSessionId)) {
                const localPlayer = players.get(localSessionId)!;
                if (localPlayer.alive) {
                    camera.follow(localPlayer.renderX, localPlayer.renderY, 0.2);
                    if (isTrialMode) {
                        camera.setPosition(
                            Math.max(0, Math.min(currentTrialArenaWidth, camera.getX())),
                            Math.max(0, Math.min(currentTrialArenaHeight, camera.getY()))
                        );
                    }
                    camera.updateZoomForMass(localPlayer.mass);
                    if (inputController) {
                        inputController.updateTouchTarget(localPlayer.renderX, localPlayer.renderY);
                        inputController.updateMouseTarget();
                    }
                }
                // Si está muerto, la cámara queda congelada en la última posición
            }

            // Muestreo de estelas: añadir punto si el jugador se movió mas de 3px
            for (const p of players.values()) {
                if (!p.alive) continue;
                let trail = trails.get(p.sessionId);
                if (!trail) {
                    trail = [];
                    trails.set(p.sessionId, trail);
                }
                // Purga puntos expirados
                while (trail.length > 0 && nowMs - trail[0].time > 200) {
                    trail.shift();
                }
                // Añadir punto nuevo si se movio lo suficiente
                const last = trail.length > 0 ? trail[trail.length - 1] : null;
                if (!last || Math.hypot(p.renderX - last.x, p.renderY - last.y) > 3) {
                    trail.push({
                        x: p.renderX,
                        y: p.renderY,
                        radius: Math.sqrt(p.mass) * 2,
                        element: p.element,
                        time: nowMs,
                        seed: Math.random(),
                    });
                }
            }
            // Limpiar estelas de jugadores desconectados
            for (const id of trails.keys()) {
                if (!players.has(id)) trails.delete(id);
            }

            // Construcción de la lista de entidades a renderizar (reciclaje in-place en pool)
            let pIdx = 0;
            for (const p of players.values()) {
                let rp = pooledRenderablePlayers[pIdx];
                if (!rp) {
                    rp = {
                        sessionId: p.sessionId,
                        renderX: p.renderX,
                        renderY: p.renderY,
                        mass: p.mass,
                        element: p.element,
                        isLocal: p.sessionId === localSessionId,
                        alive: p.alive,
                        isBot: p.isBot,
                        alias: p.alias,
                        invulnerableUntil: p.invulnerableUntil,
                        speedBoostUntil: p.speedBoostUntil,
                        hasShield: p.hasShield,
                        doubleMassUntil: p.doubleMassUntil,
                        frozenUntil: p.frozenUntil,
                        skin: p.skin,
                        cosmetics: p.sessionId === localSessionId ? currentCosmetics : undefined,
                        vx: p.vx,
                        vy: p.vy,
                        facingAngle: p.facingAngle,
                        killstreak: p.killstreak,
                        isBoosting: p.isBoosting,
                        kills: p.kills,
                        drinkingUntil: p.drinkingUntil,
                        magnetUntil: p.magnetUntil,
                        elementLockedUntil: p.elementLockedUntil,
                        potionCooldownUntil: p.potionCooldownUntil,
                        activePotionEffect: p.activePotionEffect,
                        potionSlot0: p.potionSlot0,
                        potionSlot1: p.potionSlot1,
                        potionSlot2: p.potionSlot2,
                        cosmicFoodUntil: p.cosmicFoodUntil || 0,
                        rebirthUntil: p.rebirthUntil || 0,
                        equippedTitle: p.equippedTitle || "",
                        petBuffActive: p.petBuffActive || false,
                        petBuffType: p.petBuffType || "",
                        petBuffUntil: p.petBuffUntil || 0,
                        petNextBuffAt: p.petNextBuffAt || 0,
                    };
                    pooledRenderablePlayers[pIdx] = rp;
                } else {
                    rp.sessionId = p.sessionId;
                    rp.renderX = p.renderX;
                    rp.renderY = p.renderY;
                    rp.mass = p.mass;
                    rp.element = p.element;
                    rp.isLocal = p.sessionId === localSessionId;
                    rp.alive = p.alive;
                    rp.isBot = p.isBot;
                    rp.alias = p.alias;
                    rp.invulnerableUntil = p.invulnerableUntil;
                    rp.speedBoostUntil = p.speedBoostUntil;
                    rp.hasShield = p.hasShield;
                    rp.doubleMassUntil = p.doubleMassUntil;
                    rp.frozenUntil = p.frozenUntil;
                    rp.skin = p.skin;
                    rp.cosmetics = p.sessionId === localSessionId ? currentCosmetics : undefined;
                    rp.vx = p.vx;
                    rp.vy = p.vy;
                    rp.facingAngle = p.facingAngle;
                    rp.killstreak = p.killstreak;
                    rp.isBoosting = p.isBoosting;
                    rp.kills = p.kills;
                    rp.drinkingUntil = p.drinkingUntil;
                    rp.magnetUntil = p.magnetUntil;
                    rp.elementLockedUntil = p.elementLockedUntil;
                    rp.potionCooldownUntil = p.potionCooldownUntil;
                    rp.activePotionEffect = p.activePotionEffect;
                    rp.potionSlot0 = p.potionSlot0;
                    rp.potionSlot1 = p.potionSlot1;
                    rp.potionSlot2 = p.potionSlot2;
                    rp.cosmicFoodUntil = p.cosmicFoodUntil || 0;
                    rp.rebirthUntil = p.rebirthUntil || 0;
                    rp.equippedTitle = p.equippedTitle || "";
                    rp.petBuffActive = p.petBuffActive || false;
                    rp.petBuffType = p.petBuffType || "";
                    rp.petBuffUntil = p.petBuffUntil || 0;
                    rp.petNextBuffAt = p.petNextBuffAt || 0;
                }
                pIdx++;
            }
            pooledRenderablePlayers.length = pIdx;

            // Limpiar animaciones de muerte expiradas
            for (let i = deaths.length - 1; i >= 0; i--) {
                if (nowMs - deaths[i].startTime >= DEATH_ANIMATION_MS) {
                    deaths.splice(i, 1);
                }
            }

            // Limpiar partículas de comida expiradas
            for (let i = foodParticles.length - 1; i >= 0; i--) {
                if (nowMs - foodParticles[i].startTime >= foodParticles[i].duration) {
                    foodParticles.splice(i, 1);
                }
            }

            // Limpiar anillos de respawn expirados
            for (let i = respawnRings.length - 1; i >= 0; i--) {
                if (nowMs - respawnRings[i].startTime >= respawnRings[i].duration) {
                    respawnRings.splice(i, 1);
                }
            }

            // Limpiar efectos elementales expirados
            for (let i = elementalHits.length - 1; i >= 0; i--) {
                if (nowMs - elementalHits[i].startTime >= elementalHits[i].duration) {
                    elementalHits.splice(i, 1);
                }
            }

            let fIdx = 0;
            for (const [foodId, f] of foods.entries()) {
                let rf = pooledRenderableFoods[fIdx];
                const shape = foodShapeFromId(foodId);
                if (!rf) {
                    rf = { x: f.x, y: f.y, shape };
                    pooledRenderableFoods[fIdx] = rf;
                } else {
                    rf.x = f.x;
                    rf.y = f.y;
                    rf.shape = shape;
                }
                fIdx++;
            }
            pooledRenderableFoods.length = fIdx;

        pooledRenderablePowerUps.length = 0;
        for (const pw of powerUps.values()) {
            pooledRenderablePowerUps.push(pw);
        }

        // Limpiar pickup effects expirados
        for (let i = powerUpPickups.length - 1; i >= 0; i--) {
            if (nowMs - powerUpPickups[i].startTime >= powerUpPickups[i].duration) {
                powerUpPickups.splice(i, 1);
            }
        }

        // Limpiar textos flotantes de combate expirados (Sprint 14C)
        for (let i = floatingTexts.length - 1; i >= 0; i--) {
            if (nowMs - floatingTexts[i].startTime >= floatingTexts[i].duration) {
                floatingTexts.splice(i, 1);
            }
        }

        // Filtrar meteorImpacts expirados
        for (let i = meteorImpacts.length - 1; i >= 0; i--) {
            if (nowMs - meteorImpacts[i].startTime > meteorImpacts[i].duration) {
                meteorImpacts.splice(i, 1);
            }
        }
        // Filtrar gravityCaptures expirados
        for (let i = gravityCaptures.length - 1; i >= 0; i--) {
            if (nowMs - gravityCaptures[i].startTime > gravityCaptures[i].duration) {
                gravityCaptures.splice(i, 1);
            }
        }

        // Limpiar emotes activos expirados (Sprint 30)
        for (const [sId, emote] of activeEmotes.entries()) {
            if (nowMs - emote.startTime >= emote.duration) {
                activeEmotes.delete(sId);
            }
        }

        // Deteccion de impactos de meteoritos (para spawnear MeteorImpactEffect)
        if (room && (room.state as any).meteors) {
            (room.state as any).meteors.forEach((meteor: any, meteorId: string) => {
                if (meteor.impacted && !meteorImpactedSeen.has(meteorId)) {
                    meteorImpactedSeen.add(meteorId);
                    meteorImpacts.push({
                        x: meteor.x,
                        y: meteor.y,
                        startTime: performance.now(),
                        duration: 400,
                    });
                    if (localSessionId && players.has(localSessionId)) {
                        const lp = players.get(localSessionId)!;
                        if (lp.alive && Math.hypot(meteor.x - lp.renderX, meteor.y - lp.renderY) < 350) {
                            camera.triggerShake(9, 250);
                        }
                    }
                }
            });
            for (const id of meteorImpactedSeen) {
                if (!(room.state as any).meteors.has(id)) {
                    meteorImpactedSeen.delete(id);
                }
            }
        }

        pooledRenderableMeteors.length = 0;
        if (room && (room.state as any).meteors) {
            let mIdx = 0;
            (room.state as any).meteors.forEach((m: any) => {
                let rm = pooledRenderableMeteors[mIdx];
                if (!rm) {
                    rm = {
                        x: m.x,
                        y: m.y,
                        impactAt: m.impactAt,
                        expiresAt: m.expiresAt,
                        impacted: m.impacted,
                    };
                    pooledRenderableMeteors[mIdx] = rm;
                } else {
                    rm.x = m.x;
                    rm.y = m.y;
                    rm.impactAt = m.impactAt;
                    rm.expiresAt = m.expiresAt;
                    rm.impacted = m.impacted;
                }
                mIdx++;
            });
            pooledRenderableMeteors.length = mIdx;
        }

        activeRenderer.render(
            pooledRenderablePlayers,
            pooledRenderableFoods,
            deaths,
            elementalHits,
            localSessionId,
            trails,
            absorbPulses,
            nowMs,
            foodParticles,
            respawnRings,
            currentCosmetics,
            pooledRenderablePowerUps,
            powerUpPickups,
            activeEvent,
            pooledRenderableMeteors,
            meteorImpacts,
            gravityCaptures,
            floatingTexts,
            attractModeActive,
            activeEmotes
        );

        // Actualización optimizada del HUD cada 6 frames
        frameCount++;
        if (frameCount % 6 === 0) {
            updateBuffHud();
            const localPlayer = (localSessionId && players.has(localSessionId)) ? players.get(localSessionId)! : null;
            const isLocalActiveInArena = !attractModeActive && !isSpectating && !!localPlayer && localPlayer.alive && !localPlayer.isSpectator;

            potionsUI.updateHUD(isLocalActiveInArena ? localPlayer : null);
            emotesUI.setHUDButtonVisible(isLocalActiveInArena);
            if (mobileElementBar) {
                mobileElementBar.classList.toggle("hidden", !isLocalActiveInArena);
            }
            if (playersDisplay) {
                let humanCount = 0;
                let botCount = 0;
                for (const p of players.values()) {
                    if (p.isBot) botCount++;
                    else humanCount++;
                }
                playersDisplay.textContent = `${humanCount} · ${t("hud.bots")}: ${botCount}`;
            }
            if (isPrivateRoom) {
                updatePrivateHudCount();
                if (localSessionId && players.has(localSessionId)) {
                    const localPlayer = players.get(localSessionId)!;
                    const statsEl = document.getElementById("private-hud-stats");
                    if (statsEl) {
                        const elLabels: Record<string, string> = {
                            fire: "🔥 Fuego",
                            water: "💧 Agua",
                            plant: "🌿 Planta",
                            electric: "⚡ Rayo",
                            earth: "🪨 Tierra",
                        };
                        const elLabel = elLabels[localPlayer.element] || `✨ ${localPlayer.element}`;
                        statsEl.innerHTML = `⚪ ${Math.floor(localPlayer.mass)}<span class="private-hud-el-text"> | ${elLabel}</span> | ⚔️ ${localPlayer.kills || 0}`;
                    }
                }
            }
            if (localSessionId && players.has(localSessionId)) {
                const localPlayer = players.get(localSessionId)!;
                if (localPlayer.alive && !localPlayer.isSpectator && !isSpectating) {
                    if (!isPrivateRoom && !isTrialMode) {
                        questManager.trackMass(localPlayer.mass);
                        if (nowMs - lastSurvivalTrackAt >= 1000) {
                            lastSurvivalTrackAt = nowMs;
                            const currentLifeDuration = performance.now() - localPlayer.lifeStartTime;
                            questManager.trackSurvivalTime(currentLifeDuration);
                        }
                    }
                }
                if (massDisplay) {
                    massDisplay.textContent = String(Math.round(localPlayer.mass));
                }
                if (elementDisplay) {
                    elementDisplay.textContent = t(`elements.${localPlayer.element}`, { defaultValue: localPlayer.element });
                }
                if (killsDisplay) {
                    killsDisplay.textContent = String(localPlayer.kills);
                }
                if (deathsDisplay) {
                    deathsDisplay.textContent = String(localPlayer.deaths);
                }

                // Actualizar badge de cooldown de cambio de elemento
                const cooldownDisplay = elementCooldown || document.getElementById("element-cooldown");
                if (cooldownDisplay) {
                    const now = Date.now();
                    const remaining = ELEMENT_CHANGE_COOLDOWN_MS - (now - localPlayer.lastElementChangeAt);
                    if (remaining > 0) {
                        cooldownDisplay.textContent = `🔒 ${Math.ceil(remaining / 1000)}s`;
                        cooldownDisplay.classList.add("active");
                    } else {
                        cooldownDisplay.classList.remove("active");
                    }
                }

                if (mobileElementBar) {
                    const onCd = Date.now() - localPlayer.lastElementChangeAt < ELEMENT_CHANGE_COOLDOWN_MS;
                    mobileElementBar.querySelectorAll(".mobile-el-btn").forEach((btn) => {
                        const el = (btn as HTMLElement).dataset.element;
                        btn.classList.toggle("selected", el === localPlayer.element);
                        btn.classList.toggle("cooldown", onCd && el !== localPlayer.element);
                    });
                }
                if (mobileBoostBtn) {
                    mobileBoostBtn.classList.toggle("disabled", localPlayer.mass <= BOOST_MIN_MASS);
                }
            }

            // Actualizar badge de racha
            if (streakDisplay && streakValue) {
                const streak = profileManager.getProfile().currentStreak;
                if (streak >= 2) {
                    streakDisplay.classList.remove("hidden");
                    streakValue.textContent = String(streak);
                } else {
                    streakDisplay.classList.add("hidden");
                }
            }

            // Gestión del overlay de muerte
            if (!isSpectating && localSessionId && players.has(localSessionId) && deathOverlay && respawnCountdown) {
                const localPlayer = players.get(localSessionId)!;
                if (!localPlayer.alive) {
                    // El modal ya se muestra en showDeathModal, solo actualizamos el countdown
                    if (countdownActive && !countdownPaused) {
                        const remainingMs = Math.max(0, countdownEndsAt - performance.now());
                        const remainingSec = (remainingMs / 1000).toFixed(1);
                        respawnCountdown.textContent = t("combat.respawningIn", { seconds: remainingSec });
                        // Clase urgent si queda menos de 2s
                        if (remainingMs < 2000) {
                            respawnCountdown.classList.add("urgent");
                        } else {
                            respawnCountdown.classList.remove("urgent");
                        }
                        // Si el countdown expiro, ocultar el modal (el servidor respawnea solo)
                        if (remainingMs <= 0) {
                            countdownActive = false;
                            deathOverlay.classList.add("hidden");
                        }
                    }
                    // Actualizar el timer de cooldown del rewarded si esta visible
                    if (rewardedCdTimer && rewardedCooldownBadge && !rewardedCooldownBadge.classList.contains("hidden")) {
                        if (lastRewardedUsedAt > 0) {
                            const remaining = Math.max(0, REWARDED_COOLDOWN_MS - (performance.now() - lastRewardedUsedAt));
                            const minutes = Math.floor(remaining / 60000);
                            const seconds = Math.floor((remaining % 60000) / 1000);
                            rewardedCdTimer.textContent = `${minutes}:${seconds.toString().padStart(2, "0")}`;
                        }
                    }
                } else {
                    deathOverlay.classList.add("hidden");
                    countdownActive = false;
                    countdownPaused = false;
                }
            }
        }

        // Tutorial paso 2: detectar 2s continuos de movimiento
        if (tutorialManager.getCurrentStep() === "movement") {
            if (lastMouseMoveTime > 0 && nowMs - lastMouseMoveTime < 200) {
                // Hubo movimiento reciente
                if (movementAccumMs === 0) movementAccumMs = nowMs;
                if (nowMs - movementAccumMs >= 2000) {
                    tutorialManager.completeMovement();
                    movementAccumMs = 0;
                }
            } else {
                movementAccumMs = 0;
            }
        }

        // Tutorial paso 4: detectar counter cerca o timeout de 20s
        if (tutorialManager.getCurrentStep() === "danger") {
            if (dangerTimeoutStart === 0) dangerTimeoutStart = nowMs;
            let threatNear = false;
            if (localSessionId && players.has(localSessionId)) {
                const localPlayer = players.get(localSessionId)!;
                if (localPlayer.alive) {
                    for (const other of players.values()) {
                        if (other.sessionId === localSessionId || !other.alive) continue;
                        const counterKey = `${other.element}->${localPlayer.element}`;
                        if (!ELEMENTAL_COUNTERS[counterKey]) continue;
                        const dx = other.renderX - localPlayer.renderX;
                        const dy = other.renderY - localPlayer.renderY;
                        if (dx * dx + dy * dy < 300 * 300) {
                            threatNear = true;
                            break;
                        }
                    }
                }
            }
            if (threatNear || nowMs - dangerTimeoutStart >= 20000) {
                tutorialManager.completeTutorial();
                dangerTimeoutStart = 0;
            }
        }

        // Registrar tiempo jugado cada 5s
        if (localSessionId && players.has(localSessionId) && !document.hidden) {
            const localPlayer = players.get(localSessionId)!;
            if (localPlayer.alive && nowMs - lastPlayTimeUpdate >= PLAY_TIME_UPDATE_INTERVAL_MS) {
                const delta = nowMs - lastPlayTimeUpdate;
                if (!isPrivateRoom && !isTrialMode) {
                    profileManager.recordPlayTime(delta, localPlayer.element);
                    profileManager.save(false);
                }
                lastPlayTimeUpdate = nowMs;
            }
        } else {
            // Si esta muerto o la pestaña oculta, resetear el marcador para no acumular
            lastPlayTimeUpdate = nowMs;
        }

        // Actualizar ranking cada 500ms
        if (nowMs - lastRankingUpdate >= RANKING_UPDATE_INTERVAL_MS) {
            leaderboardUI.update(players, localSessionId);
            lastRankingUpdate = nowMs;
        }
        } catch (err) {
            console.error("[GameLoop] Error recuperado:", err);
        }
    }

    // Fix: si el paso actual es identity pero el tooltip no se muestra (porque ya se vio antes),
    // avanzar directamente al paso 2 para no quedar atascado.
    if (tutorialManager.getCurrentStep() === "identity") {
        const tooltipVisible = spawnTooltip && !spawnTooltip.classList.contains("hidden");
        if (!tooltipVisible) {
            tutorialManager.completeIdentity();
        }
    }

    if (startAsSpectator) {
        enterSpectatorMode();
    }
}



// Ejecución controlada sin top-level await
main().catch((err) => {
    console.error("[Main] Error fatal en la aplicación cliente:", err);
});

window.addEventListener("beforeunload", () => {
    if (!isPrivateRoom && !isTrialMode) {
        profileManager.save(true);
        void profileManager.syncToDatabase();
    }
});

document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
        if (!isPrivateRoom && !isTrialMode) {
            profileManager.save(true);
        }
    } else {
        // Al volver a visible, resetear el marcador de tiempo
        lastPlayTimeUpdate = performance.now();
    }
});

// Detectar si estamos en la página de reset exitoso de Colyseus
if (window.location.pathname.includes('/auth/reset-password') && window.location.search.includes('success')) {
    import('sweetalert2').then(({ default: Swal }) => {
        Swal.fire({
            icon: 'success',
            title: 'Contraseña Restablecida',
            text: 'Serás redirigido al juego en 3 segundos...',
            timer: 3000,
            showConfirmButton: false,
            allowOutsideClick: false,
        }).then(() => {
            window.location.href = '/';
        });
    }).catch(() => {
        setTimeout(() => {
            window.location.href = '/';
        }, 3000);
    });
}

